import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  Paper,
  Stack,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Drawer,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  SmartToy as BotIcon,
  Psychology as BrainIcon,
  Extension as ToolIcon,
  Security as SecurityIcon,
  Tune as TuneIcon,
  Chat as ChatIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Memory as MemoryIcon,
  Build as BuildIcon,
  Shield as ShieldIcon,
  MenuBook as KnowledgeIcon,
  Folder as FolderIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Api as ApiIcon,
  ContentCopy as CopyIcon,
  Description as DocsIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Code as CodeIcon,
  Visibility as EyeIcon,
  VpnKey as KeyIcon,
  Delete as DeleteIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { agentsService } from '../services/agentsService';
import { integrationsService } from '../services/integrationsService';
import { externalApiService } from '../services/externalApiService';
import { authService } from '../services/authService';
import { ragService } from '../services/ragService';
import ToolsConfiguration from '../components/ToolsConfiguration';
import AgentTester from '../components/AgentTester';
import AgentDiagnosticsV5 from '../components/AgentDiagnosticsV5';
import TraceDrawer from '../components/TraceDrawer';
import DataCollectionTab from '../components/DataCollectionTab';
import AgentSessionsTab from '../components/AgentSessionsTab';
import OutputSchemaEditor from '../components/OutputSchemaEditor';

// Componente Drawer de Documentação
const DocumentationDrawer = ({ open, onClose, agentName, responseExample, companyInfo, isStreamingAgent = false, outputSchema = null }) => {
  const [expandedSection, setExpandedSection] = useState('intro');
  const [copiedSection, setCopiedSection] = useState(null);

  const handleSectionClick = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleCopyCode = (code, sectionId) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedSection(sectionId);
      setTimeout(() => setCopiedSection(null), 2000);
    });
  };

  const CodeBlock = ({ children, copyable = false, sectionId = null }) => (
    <Box sx={{ 
      position: 'relative',
      bgcolor: 'grey.900', 
      color: 'white', 
      p: 2, 
      borderRadius: 1, 
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
      maxHeight: '300px',
      border: '1px solid rgba(255,255,255,0.1)'
    }}>
      {copyable && (
        <IconButton
          size="small"
          onClick={() => handleCopyCode(children, sectionId)}
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
          }}
        >
          {copiedSection === sectionId ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
        </IconButton>
      )}
      {children}
    </Box>
  );

  const InfoBox = ({ type = 'info', children }) => {
    const colors = {
      info: { bg: 'info.light', text: 'info.contrastText' },
      warning: { bg: 'warning.light', text: 'warning.contrastText' },
      success: { bg: 'success.light', text: 'success.contrastText' },
      error: { bg: 'error.light', text: 'error.contrastText' }
    };
    
    return (
      <Box sx={{ 
        bgcolor: colors[type].bg, 
        color: colors[type].text, 
        p: 2, 
        borderRadius: 1, 
        mb: 2,
        border: `1px solid ${colors[type].bg}`
      }}>
        {children}
      </Box>
    );
  };

  const documentationSections = [
    {
      id: 'intro',
      title: '🚀 Introdução',
      icon: <InfoIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="primary">
            🤖 API Externa organIA
          </Typography>
          <Typography variant="body1" paragraph>
            Integre seus agentes em qualquer aplicação com nossa API RESTful segura.
          </Typography>
          
          {/* Alerta do Modo do Agente */}
          <Alert 
            severity={isStreamingAgent ? "info" : "success"} 
            variant="outlined" 
            sx={{ mb: 2, bgcolor: isStreamingAgent ? 'info.50' : 'success.50' }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {isStreamingAgent ? '⚡ MODO STREAMING ATIVO' : '🔄 MODO TRADICIONAL'}
            </Typography>
            <Typography variant="body2">
              {isStreamingAgent 
                ? 'Este agente mostra a resposta sendo "digitada" em tempo real, como no ChatGPT. Ideal para chats e interfaces interativas.'
                : 'Este agente retorna a resposta completa de uma vez. Ideal para WhatsApp, integrações automáticas e sistemas corporativos.'
              }
            </Typography>
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InfoBox type="success">
                <Typography variant="subtitle2" gutterBottom>✅ Recursos Principais</Typography>
                <Typography variant="body2">
                  • Autenticação via API Key<br/>
                  • Rate Limiting inteligente<br/>
                  • Billing automático<br/>
                  {isStreamingAgent 
                    ? '• Streaming em tempo real (SSE)'
                    : '• Resposta JSON tradicional'
                  }
                </Typography>
              </InfoBox>
            </Grid>
            <Grid item xs={6}>
              <InfoBox type="info">
                <Typography variant="subtitle2" gutterBottom>🎯 Ideal Para</Typography>
                <Typography variant="body2">
                            {isStreamingAgent ? (
            <>• Sites e apps com chat<br/>• Interfaces interativas<br/>• Experiência "ChatGPT-like"<br/>• Respostas em tempo real</>
          ) : (
            <>• WhatsApp Business<br/>• Integrações automáticas<br/>• Sistemas corporativos<br/>• Webhooks e APIs</>
          )}
                </Typography>
              </InfoBox>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            📡 Endpoint
          </Typography>
          <CodeBlock copyable sectionId="base-url">
{`POST ${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000'}/external/v1/chat`}
          </CodeBlock>
        </Box>
      )
    },
    {
      id: 'auth',
      title: '🔐 Autenticação',
      icon: <ShieldIcon />,
      render: () => (
        <Box>
          <Typography variant="body1" paragraph>
            Inclua sua API Key no header Authorization em todas as requisições.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            📋 Headers {isStreamingAgent ? 'Obrigatórios' : 'Obrigatório'}
          </Typography>
          <CodeBlock copyable sectionId="auth-header">
{`Authorization: Bearer pk_live_sua_chave_completa_aqui${isStreamingAgent ? '\nAccept: text/event-stream' : ''}`}
          </CodeBlock>
          
          {isStreamingAgent && (
            <InfoBox type="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>⚡ Header para Streaming</Typography>
              <Typography variant="body2">
                O header <code>Accept: text/event-stream</code> diz para a API que você quer receber a resposta "token por token" em tempo real. Sem ele, a API enviará a resposta completa de uma vez.
              </Typography>
            </InfoBox>
          )}

          <InfoBox type="warning">
            <Typography variant="subtitle2" gutterBottom>⚠️ IMPORTANTE</Typography>
            <Typography variant="body2">
              • Use a chave COMPLETA, não o preview mascarado<br/>
              • A chave é validada via hash SHA-256 a cada requisição<br/>
              • Mantenha sua API Key segura e nunca a exponha em código cliente
            </Typography>
          </InfoBox>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            🔍 Exemplo de Requisição {isStreamingAgent ? '(Streaming)' : '(Tradicional)'}
          </Typography>
          <CodeBlock copyable sectionId="auth-example">
{`curl ${isStreamingAgent ? '-N ' : ''}-X POST "${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000'}/external/v1/chat" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer pk_live_7gCw9Fn0JCpv2Gix-ATK3l_BjKk3dkjl" \\${isStreamingAgent ? '\n  -H "Accept: text/event-stream" \\' : ''}
  -d '{"agent_name":"${agentName}","message":"Olá!"}'`}
          </CodeBlock>
        </Box>
      )
    },
    {
      id: 'fields',
      title: '📝 Campos da Requisição',
      icon: <CodeIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="primary">
            🔸 Campos Obrigatórios
          </Typography>
          
          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                agent_name (string)
              </Typography>
              <Typography variant="body2" gutterBottom>
                Nome exato do agente como cadastrado na sua empresa
              </Typography>
              <CodeBlock copyable sectionId="agent-name">
{`"agent_name": "${agentName}"`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                message (string)
              </Typography>
              <Typography variant="body2" gutterBottom>
                Mensagem/pergunta do usuário final que será processada pelo agente
              </Typography>
              <CodeBlock copyable sectionId="message">
{`"message": "Qual o prazo de entrega para São Paulo?"`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                end_user_id (string)
              </Typography>
              <Typography variant="body2" gutterBottom>
                Identificador único do usuário final (usado para histórico)
              </Typography>
              <CodeBlock copyable sectionId="end-user-id">
{`// Exemplos:
"end_user_id": "5511999887766"        // WhatsApp
"end_user_id": "cliente@empresa.com"  // Email
"end_user_id": "user_12345"          // ID do Sistema`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                channel (string)
              </Typography>
              <Typography variant="body2" gutterBottom>
                Canal de origem da requisição (afeta analytics e personalização)
              </Typography>
              <CodeBlock copyable sectionId="channel">
{`"channel": "api"        // API externa
"channel": "whatsapp"   // WhatsApp Business
"channel": "telegram"   // Telegram Bot
"channel": "web"        // Site/WebApp`}
              </CodeBlock>
            </Paper>
          </Stack>

          <Typography variant="h6" gutterBottom color="secondary" sx={{ mt: 3 }}>
            🔹 Campos Opcionais
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="secondary" gutterBottom>
                thread_id (string | null)
              </Typography>
              <Typography variant="body2" gutterBottom>
   Controle de continuidade da conversa
              </Typography>
              <InfoBox type="info">
                <Typography variant="body2">
                  • <strong>null ou omitido</strong>: Inicia NOVA conversa<br/>
                  • <strong>"uuid-string"</strong>: CONTINUA conversa existente<br/>
                  • Retornado na primeira resposta para reutilização
                </Typography>
              </InfoBox>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="secondary" gutterBottom>
                metadata (object)
              </Typography>
              <Typography variant="body2" gutterBottom>
                Contexto adicional para personalização inteligente do agente
              </Typography>
              <CodeBlock copyable sectionId="metadata">
{`"metadata": {
     "campaign_id": "black_friday_2024",  // Mencionar promoção
     "source": "instagram",               // Tom mais casual
     "user_location": "São Paulo",        // Frete e prazos
     "priority": "vip",                   // Atendimento especial
     "user_type": "lead",                 // Foco em conversão
     "product_interest": "plano_pro",     // Destacar recursos
     "referrer": "influencer_abc"         // Oferta especial
}`}
              </CodeBlock>
            </Paper>
          </Stack>
        </Box>
      )
    },
    {
      id: 'response',
      title: '📤 Response Esperado',
      icon: <EyeIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="primary">
            📋 {isStreamingAgent ? 'Eventos de Streaming (SSE)' : 'Estrutura da Resposta JSON'}
          </Typography>
          
          {isStreamingAgent ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="info.main">
                ⚡ Eventos enviados via Server-Sent Events:
              </Typography>
              
              <CodeBlock copyable sectionId="streaming-tokens">
{`# 📡 Eventos de token (em tempo real):
data: {"type": "token", "data": {"token": "Olá", "request_id": "req_123"}}
data: {"type": "token", "data": {"token": "! Como", "request_id": "req_123"}}
data: {"type": "token", "data": {"token": " posso", "request_id": "req_123"}}
data: {"type": "token", "data": {"token": " ajudar?", "request_id": "req_123"}}`}
              </CodeBlock>
              
              <CodeBlock copyable sectionId="streaming-complete">
{`# 🏁 Evento final (com dados completos):
data: {"type": "complete", "data": {
  "message": "Olá! Como posso ajudar?",
  "thread_id": "uuid-da-conversa",
  "agent_name": "${agentName}",
  "tokens_used": 45,
  "credits_charged": 0.9,
  "request_id": "req_123",
  "processing_time_ms": 1250,
  "model_used": "gpt-4o-mini",
  "streaming": true
}}`}
              </CodeBlock>
            </Stack>
          ) : (
            <CodeBlock copyable sectionId="response-structure">
{outputSchema ? `{
  "message": "{\\"${outputSchema.properties?.[0]?.name || 'campo'}\\":\\"valor\\",  ...}",
  "thread_id": "uuid-da-conversa",
  "agent_name": "${agentName}",
  "tokens_used": 3221,
  "credits_charged": 3.22,
  "request_id": "req_identificador_unico",
  "processing_time_ms": 6423,
  "model_used": "gpt-4o-mini",
  "output_type": "structured",
  "structured_output": {
    ${outputSchema.properties?.map(prop => `"${prop.name}": ${
      prop.type === 'string' ? '"exemplo"' :
      prop.type === 'number' ? '0.95' :
      prop.type === 'integer' ? '5' :
      prop.type === 'boolean' ? 'true' :
      prop.type === 'enum' ? `"${prop.enum_values?.[0] || 'valor'}"` :
      prop.type === 'array' ? '["item1", "item2"]' :
      '"valor"'
    }`).join(',\n    ')}
  }
}` : `{
  "message": "Resposta completa do agente",
  "thread_id": "uuid-da-conversa",
  "agent_name": "${agentName}",
  "tokens_used": 3221,
  "credits_charged": 3.22,
  "request_id": "req_identificador_unico",
  "processing_time_ms": 6423,
  "model_used": "gpt-4o-mini",
  "output_type": "text"
}`}
            </CodeBlock>
          )}

          <Typography variant="h6" gutterBottom color="secondary" sx={{ mt: 3 }}>
            🔹 Descrição dos Campos
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                message (string)
              </Typography>
              <Typography variant="body2">
                Resposta completa gerada pelo agente para a mensagem do usuário
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" color="info.main" gutterBottom>
                thread_id (string)
              </Typography>
              <Typography variant="body2">
                <strong>Importante:</strong> Salve este valor para continuar conversas futuras com contexto
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                tokens_used (number) • credits_charged (number)
              </Typography>
              <Typography variant="body2">
                <strong>Billing:</strong> Total de tokens processados e créditos debitados em tempo real
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" gutterBottom>
                Campos Técnicos
              </Typography>
              <Typography variant="body2">
                • <strong>model_used:</strong> Modelo LLM usado pelo agente<br/>
                • <strong>processing_time_ms:</strong> Latência total da requisição<br/>
                • <strong>request_id:</strong> Identificador único para rastreamento
              </Typography>
            </Paper>
            
            {outputSchema && (
              <>
                <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>
                    output_type (string)
                  </Typography>
                  <Typography variant="body2">
                    <strong>"structured"</strong> quando o agente retorna JSON estruturado ou <strong>"text"</strong> para texto simples
                  </Typography>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>
                    structured_output (object)
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>⚠️ IMPORTANTE:</strong> Use este campo para acessar os dados estruturados!
                  </Typography>
                  <Typography variant="body2">
                    Objeto JSON com o schema definido <strong>{outputSchema.name}</strong> contendo:
                  </Typography>
                  <Box sx={{ pl: 2, mt: 1 }}>
                    {outputSchema.properties?.map((prop, idx) => (
                      <Typography key={idx} variant="caption" component="div" sx={{ mb: 0.5 }}>
                        • <strong>{prop.name}</strong> ({prop.type}): {prop.description || 'Campo do schema'}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </>
            )}
          </Stack>

          <InfoBox type="success" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              🎯 Dicas de Implementação {isStreamingAgent ? '(Streaming)' : '(Tradicional)'}
            </Typography>
            <Typography variant="body2">
              • Use <strong>thread_id</strong> para manter contexto entre mensagens<br/>
              • Monitore <strong>tokens_used</strong> para otimizar custos<br/>
              • <strong>processing_time_ms</strong> ajuda a medir performance<br/>
              {isStreamingAgent ? (
                <>• Use JavaScript <strong>EventSource</strong> para conectar<br/>
                • Atualize sua interface a cada <strong>token</strong> recebido<br/>
                • Finalize quando receber o evento <strong>complete</strong></>
              ) : (
                <>• Resposta vem completa no campo <strong>message</strong><br/>
                • Salve o <strong>thread_id</strong> para continuar conversas<br/>
                • Monitore <strong>credits_charged</strong> para controlar custos</>
              )}
            </Typography>
          </InfoBox>
        </Box>
      )
    },
    {
      id: 'errors',
      title: '🚨 Tratamento de Erros',
      icon: <WarningIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="error">
            🚨 Códigos de Erro HTTP
          </Typography>
          
          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: 'error.50', borderLeft: '4px solid', borderColor: 'error.main' }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                401 - Unauthorized
              </Typography>
              <Typography variant="body2" gutterBottom>
                API Key inválida, expirada ou mal formatada
              </Typography>
              <CodeBlock copyable sectionId="error-401">
{`{
  "detail": "API key inválida: Chave não encontrada ou inativa"
}`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                403 - Forbidden
              </Typography>
              <Typography variant="body2" gutterBottom>
                Acesso negado (agente não permitido, canal restrito, IP bloqueado)
              </Typography>
              <CodeBlock copyable sectionId="error-403">
{`{
  "detail": "Agente 'Bot Vendas' não permitido para esta API Key"
}`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
              <Typography variant="subtitle2" color="info.main" gutterBottom>
                422 - Unprocessable Entity
              </Typography>
              <Typography variant="body2" gutterBottom>
                Dados de entrada inválidos ou malformados
              </Typography>
              <CodeBlock copyable sectionId="error-422">
{`{
  "detail": "Dados inválidos: Campo 'agent_name' é obrigatório"
}`}
              </CodeBlock>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: 'grey.100', borderLeft: '4px solid', borderColor: 'grey.500' }}>
              <Typography variant="subtitle2" color="grey.700" gutterBottom>
                429 - Rate Limit Exceeded
              </Typography>
              <Typography variant="body2" gutterBottom>
                Limite de requisições excedido para seu tier
              </Typography>
              <CodeBlock copyable sectionId="error-429">
{`{
  "detail": "Rate limit excedido para tier 'basic'. Limite: 60 requests por minuto. Reset em: 1642800000"
}`}
              </CodeBlock>
            </Paper>
          </Stack>

          <InfoBox type="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>⚡ Implementação de Retry</Typography>
            <Typography variant="body2">
              Para erros <strong>429</strong>, implemente retry com backoff exponencial.<br/>
              Para outros erros, verifique a configuração antes de tentar novamente.
            </Typography>
          </InfoBox>
        </Box>
      )
    },
    ...(isStreamingAgent ? [{
      id: 'streaming',
      title: '⚡ Implementação Streaming',
      icon: <CodeIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="primary">
            ⚡ Como Implementar Streaming
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            💻 Exemplo JavaScript Simples
          </Typography>
          <CodeBlock copyable sectionId="js-streaming">
{`// Conectar e receber resposta em tempo real
const eventSource = new EventSource('/external/v1/chat');

let resposta = '';

eventSource.onmessage = function(event) {
  const dados = JSON.parse(event.data);
  
  if (dados.type === 'token') {
    resposta += dados.data.token;
    document.getElementById('chat').innerHTML = resposta; // Atualizar na tela
  } 
  
  if (dados.type === 'complete') {
    console.log('Resposta completa!');
    eventSource.close();
  }
};`}
          </CodeBlock>
          

          
          <InfoBox type="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>💡 Por que usar Streaming?</Typography>
            <Typography variant="body2">
              • <strong>Experiência melhor:</strong> Como conversar com uma pessoa real<br/>
              • <strong>Mais interativo:</strong> Usuário vê a resposta chegando<br/>
              • <strong>Menos espera:</strong> Parece mais rápido que aguardar tudo<br/>
              • <strong>Mesmo custo:</strong> Preço idêntico ao modo tradicional
            </Typography>
          </InfoBox>
        </Box>
      )
    }] : []),
    {
      id: 'headers',
      title: '📊 Rate Limiting',
      icon: <InfoIcon />,
      render: () => (
        <Box>
          <Typography variant="h6" gutterBottom color="primary">
            🚦 Limites da Sua Empresa
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InfoBox type="info">
                <Typography variant="subtitle2" gutterBottom>
                  📋 {companyInfo?.company?.name || 'Sua Empresa'}
                </Typography>
                <Typography variant="body1" color="primary">
                  <strong>Tier:</strong> {companyInfo?.rate_limits?.display_name || 'Básico'}
                </Typography>
              </InfoBox>
            </Grid>
            <Grid item xs={6}>
              <InfoBox type="success">
                <Typography variant="subtitle2" gutterBottom>📊 Limites Atuais</Typography>
                <Typography variant="body2">
                  • <strong>Por minuto:</strong> {(companyInfo?.rate_limits?.requests_per_minute || 60).toLocaleString()}<br/>
                  • <strong>Por hora:</strong> {(companyInfo?.rate_limits?.requests_per_hour || 1000).toLocaleString()}<br/>
                  • <strong>Por dia:</strong> {(companyInfo?.rate_limits?.requests_per_day || 10000).toLocaleString()}<br/>
                  • <strong>Burst:</strong> {(companyInfo?.rate_limits?.burst_allowance || 100).toLocaleString()}
                </Typography>
              </InfoBox>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom color="secondary" sx={{ mt: 3 }}>
            📡 Headers de Resposta
          </Typography>
          
          <CodeBlock copyable sectionId="rate-headers">
{`X-RateLimit-Limit: ${companyInfo?.rate_limits?.requests_per_hour || '1000'}
X-RateLimit-Remaining: 999
X-RateLimit-Reset: ${Math.floor(Date.now() / 1000) + 3600}
X-RateLimit-Tier: ${companyInfo?.rate_limits?.tier_name || 'basic'}`}
          </CodeBlock>

          <InfoBox type="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>💡 Dicas Importantes</Typography>
            <Typography variant="body2">
              • Monitore os headers para evitar erros 429<br/>
              • Implemente retry com backoff exponencial<br/>
              • Rate limits variam por empresa e tier<br/>
              {companyInfo?.rate_limits?.tier_name === 'basic' 
                ? '• Considere upgrade de tier se necessário' 
                : '• Tier atual oferece limites mais altos'}
            </Typography>
          </InfoBox>
        </Box>
      )
    }
  ];

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '400px', md: '500px' },
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          zIndex: 1200, // Abaixo do gerador de código
        },
        '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
      }}
      ModalProps={{ keepMounted: true }}
      transitionDuration={300}
    >
      {/* Header do Drawer */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 3,
        bgcolor: 'primary.main',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DocsIcon />
          <Box>
            <Typography variant="h6">Documentação API</Typography>
            <Typography variant="caption">Integração com {agentName}</Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Conteúdo da Documentação */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {documentationSections.map((section) => (
          <Accordion 
            key={section.id}
            expanded={expandedSection === section.id}
            onChange={() => handleSectionClick(section.id)}
            sx={{ 
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 }
            }}
          >
            <AccordionSummary sx={{ 
              px: 3, 
              py: 2,
              '&.Mui-expanded': { minHeight: 48 },
              '& .MuiAccordionSummary-content': { margin: '12px 0' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {section.icon}
                <Typography variant="subtitle1" fontWeight="medium">
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, py: 0, pb: 2 }}>
              {section.render ? (
                // Nova renderização customizada
                section.render()
              ) : (
                // Renderização legacy para compatibility
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1
                }}
              >
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontFamily: section.id === 'response' || section.id === 'errors' || section.id === 'headers' 
                      ? 'Monaco, "Cascadia Code", "Roboto Mono", monospace' 
                      : 'inherit',
                    fontSize: section.id === 'response' || section.id === 'errors' || section.id === 'headers' 
                      ? '0.75rem' 
                      : '0.875rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                    color: 'text.primary'
                  }}
                >
                  {section.content}
                </Typography>
              </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Footer com ações */}
      <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            💡 Esta documentação foi gerada automaticamente para o agente <strong>{agentName}</strong>
          </Alert>
          <Button 
            variant="outlined" 
            onClick={onClose}
            fullWidth
          >
            Fechar Documentação
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

// Componente do Drawer Gerador de Código (seguindo padrão do AgentTester)
const CodeGeneratorDrawer = ({
  open, 
  onClose, 
  agentName,
  formData, // 🆕 Receber formData para detectar streaming
  availableApiKeys, 
  selectedApiKeys, 
  setSelectedApiKeys,
  generatedCode,
  setGeneratedCode,
  codeLanguage,
  setCodeLanguage,
  documentationOpen,
  setDocumentationOpen
}) => {
  const [copied, setCopied] = useState(false);

  const codeLanguages = [
    { value: 'curl', label: 'cURL' },
    { value: 'javascript', label: 'JavaScript' },
  ];

  const generateCode = async () => {
    const firstSelectedKeyId = selectedApiKeys.length > 0 ? selectedApiKeys[0] : null;
    const selectedKeyData = availableApiKeys.find(key => key.id === firstSelectedKeyId);
    if (!selectedKeyData || !agentName || !formData) {
      console.warn('Dados insuficientes para gerar código:', { selectedKeyData: !!selectedKeyData, agentName, formData: !!formData, selectedApiKeys: selectedApiKeys.length });
      return;
    }

    // 🔍 DETECTAR SE AGENTE TEM STREAMING HABILITADO
    const isStreamingAgent = formData.streaming_enabled || false; // ✅ Baseado na configuração do agente
    
    // 🔍 DETECTAR SE AGENTE TEM STRUCTURED OUTPUT
    const hasStructuredOutput = formData.output_type_config && Object.keys(formData.output_type_config).length > 0;
    const outputSchema = hasStructuredOutput ? formData.output_type_config : null;
    
    console.log(`🔍 Gerando código para agente: ${agentName}, Streaming: ${isStreamingAgent}, Structured Output: ${hasStructuredOutput}`);

    // 🎯 GERAR EXEMPLO DE RESPOSTA baseado no tipo de output
    const generateResponseExample = () => {
      if (hasStructuredOutput && outputSchema) {
        // Exemplo com structured output
        const exampleData = {};
        outputSchema.properties?.forEach(prop => {
          switch (prop.type) {
            case 'string':
              exampleData[prop.name] = 'exemplo_texto';
              break;
            case 'number':
              exampleData[prop.name] = 0.95;
              break;
            case 'integer':
              exampleData[prop.name] = 5;
              break;
            case 'boolean':
              exampleData[prop.name] = true;
              break;
            case 'enum':
              exampleData[prop.name] = prop.enum_values?.[0] || 'valor1';
              break;
            case 'array':
              exampleData[prop.name] = ['item1', 'item2'];
              break;
            default:
              exampleData[prop.name] = 'valor';
          }
        });
        
        return `# 📋 RESPOSTA ESPERADA (Structured Output):
# {
#   "message": "${JSON.stringify(exampleData).replace(/"/g, '\\"')}",  // ← JSON stringified
#   "thread_id": "uuid-gerado",
#   "agent_name": "${agentName}",
#   "tokens_used": 45,
#   "credits_charged": 2.3,
#   "request_id": "req_123abc",
#   "processing_time_ms": 1250,
#   "model_used": "gpt-4o-mini",
#   "output_type": "structured",
#   "structured_output": ${JSON.stringify(exampleData, null, '#   ')}
# }
#
# ⚠️ IMPORTANTE: Use o campo 'structured_output' para acessar os dados!
# ✅ Exemplo: response.structured_output.${outputSchema.properties?.[0]?.name || 'campo'}`;
      } else {
        // Exemplo com texto simples
        return `# 📋 RESPOSTA ESPERADA (Text Output):
# {
#   "message": "Olá! Como posso ajudar?",
#   "thread_id": "uuid-gerado",
#   "agent_name": "${agentName}",
#   "tokens_used": 45,
#   "credits_charged": 2.3,
#   "request_id": "req_123abc",
#   "processing_time_ms": 1250,
#   "model_used": "gpt-4o-mini",
#   "output_type": "text"
# }
#
# ✅ Use o campo 'message' para ler a resposta do agente`;
      }
    };

    // Usar dados reais da API Key selecionada
    const keyDisplayName = selectedKeyData.key_name || "API Key não selecionada";
    const keyPrefix = selectedKeyData.key_prefix || "pk_live_";
    
    let dynamicApiKey = `${keyPrefix}SEU_TOKEN_COMPLETO_AQUI`;
    
    // 🔐 Tentar buscar a chave real descriptografada para geração de código
    try {
      console.log('🔐 Buscando chave descriptografada para:', selectedKeyData.id);
      const decryptedResponse = await externalApiService.getDecryptedApiKey(selectedKeyData.id);
      
      if (decryptedResponse && decryptedResponse.api_key) {
        dynamicApiKey = decryptedResponse.api_key;
        console.log('✅ Chave real obtida para geração de código');
      } else {
        console.warn('⚠️ Não foi possível obter chave real, usando placeholder');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar chave descriptografada:', error);
      // Fallback para placeholder se não conseguir buscar a chave real
    }
    
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    
    // 🔧 FALLBACK INTELIGENTE: Detectar ambiente automaticamente
    let baseUrl = process.env.REACT_APP_EXTERNAL_API_BASE_URL;
    
    if (!baseUrl) {
      if (isDevelopment) {
        baseUrl = 'http://localhost:8000';
      } else {
        // Em produção, usar mesmo host/port do frontend mas sem /api/v1
        const currentOrigin = window.location.origin;
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        
        if (apiBaseUrl && apiBaseUrl.includes('/api/v1')) {
          baseUrl = apiBaseUrl.replace('/api/v1', '');
        } else {
          baseUrl = currentOrigin.replace(/:\d+$/, ':9498'); // Assumir porta 9498 para backend
        }
      }
    }
    
    // Função helper para gerar comentários comuns
    const getCommonComments = () => ({
      agentName: "Nome exato do agente (deve existir na sua empresa)",
      message: "Mensagem que o usuário está enviando para o agente",
      endUserId: "ID único do usuário (telefone WhatsApp, email, ID interno)",
      channel: "Canal de origem: 'api', 'whatsapp', 'telegram', 'web' (usado para analytics)",
      threadId: "null = nova conversa | 'uuid' = continuar conversa existente",
      metadata: "Informações extras (campaign_id, source, location, etc.) - contexto personalizado"
    });
    
    const comments = getCommonComments();
    
    // Funções para gerar código específico de cada linguagem
    // 🆕 FUNÇÃO PARA GERAR CÓDIGO BASEADO NO MODO
    const generateCurlCode = () => {
        if (isStreamingAgent) {
            return generateStreamingCurlCode();
        } else {
            return generateTraditionalCurlCode();
        }
    };
    
    // 🔄 FUNÇÃO CURL TRADICIONAL
    const generateTraditionalCurlCode = () => `# 🔄 TRADITIONAL MODE - Chat com agente via API Externa
# 📋 API KEY CONFIGURADA: ${keyDisplayName}
# 🔑 API KEY NO EXEMPLO: ${dynamicApiKey}
# 
# ✨ MODO TRADICIONAL: Agente configurado com streaming_enabled=false
# 📝 RESPOSTA COMPLETA: Recebe toda a resposta de uma só vez
# ${isDevelopment 
  ? '✅ DESENVOLVIMENTO: Esta é uma API Key REAL de teste funcional!'
  : '⚠️  PRODUÇÃO: Substitua pela sua API Key REAL da configuração "' + keyDisplayName + '"'
}

curl -X POST "${baseUrl}/external/v1/chat" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${dynamicApiKey}" \\
  -d '{
    "agent_name": "${agentName}",
    "message": "Olá! Como você pode me ajudar?",
    "end_user_id": "user_123",
    "channel": "api",
    "thread_id": null,
    "metadata": {}
  }'

${generateResponseExample()}

# 📊 CARACTERÍSTICAS DO MODO TRADICIONAL:
# • Resposta completa em uma só requisição
# • Ideal para integração com sistemas legados
# • Menor latência percebida para respostas curtas
# • Billing e trace completos na resposta`;

    // ⚡ FUNÇÃO CURL STREAMING
    const generateStreamingCurlCode = () => `# ⚡ STREAMING MODE - Chat com agente via API Externa
# 📋 API KEY CONFIGURADA: ${keyDisplayName}
# 🔑 API KEY NO EXEMPLO: ${dynamicApiKey}
# 
# ✨ MODO STREAMING: Agente configurado com streaming_enabled=true
# 📡 EVENTOS SSE: Tokens são enviados em tempo real via Server-Sent Events
# ${isDevelopment 
  ? '✅ DESENVOLVIMENTO: Esta é uma API Key REAL de teste funcional!'
  : '⚠️  PRODUÇÃO: Substitua pela sua API Key REAL da configuração "' + keyDisplayName + '"'
}

curl -N -X POST "${baseUrl}/external/v1/chat" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${dynamicApiKey}" \\
  -H "Accept: text/event-stream" \\
  -d '{
    "agent_name": "${agentName}",
    "message": "Explique IA em 3 parágrafos",
    "end_user_id": "user_123",
    "channel": "api",
    "thread_id": null,
    "metadata": {}
  }'

# 📡 RESPOSTA ESPERADA (Eventos SSE):
# data: {"type": "token", "data": {"token": "A", "timestamp": 1234567890.123}}
# data: {"type": "token", "data": {"token": " Inteligência", "timestamp": 1234567890.124}}
# data: {"type": "token", "data": {"token": " Artificial", "timestamp": 1234567890.125}}
# ...continua com cada token...
# data: {"type": "complete", "data": {"message": "A Inteligência Artificial...", "thread_id": "uuid", "tokens_used": 150, "credits_charged": 4.2}}

# 📋 CARACTERÍSTICAS DO STREAMING:
# • Tokens chegam conforme são gerados pelo agente
# • Interface pode mostrar resposta "sendo digitada"
# • Melhor experiência do usuário para respostas longas
# • Billing e trace são enviados no evento "complete"
# • Header Accept: text/event-stream obrigatório
# • Flag -N no curl para não bufferizar`;

    // 🆕 FUNÇÃO PARA GERAR JAVASCRIPT BASEADO NO MODO
    const generateJavaScriptCode = () => {
        if (isStreamingAgent) {
            return generateStreamingJavaScriptCode();
        } else {
            return generateTraditionalJavaScriptCode();
        }
    };
    
    // 🔄 FUNÇÃO JAVASCRIPT TRADICIONAL
    const generateTraditionalJavaScriptCode = () => `/**
 * 🔄 TRADITIONAL MODE - Cliente JavaScript para API Externa
 * 📋 API KEY CONFIGURADA: ${keyDisplayName}
 * 🔑 API KEY GERADA: ${dynamicApiKey}
 * 
 * ⚠️  ATENÇÃO: Esta é uma API Key VÁLIDA gerada para este exemplo.
 * ⚠️  Substitua pela sua API Key REAL da configuração "${keyDisplayName}"
 */

// 🔧 Configuração da API
const API_KEY = "${dynamicApiKey}";  // ⚠️ SUBSTITUA pela sua API Key REAL!
const BASE_URL = "${baseUrl}";
const AGENT_NAME = "${agentName}";  // Nome exato do seu agente

/**
 * Envia mensagem para o agente via API Externa
 * @param {string} message - Mensagem do usuário para o agente
 * @param {string} endUserId - ID único do usuário (telefone, email, etc.)
 * @param {string|null} threadId - ID da conversa (null = nova conversa)
 * @param {string} channel - Canal de origem (api, whatsapp, telegram, web)
 * @param {object} metadata - Contexto adicional (campaign_id, source, etc.)
 * @returns {Promise<object|null>} Resposta do agente ou null se erro
 */
async function chatWithAgent(
    message, 
    endUserId = "user_123", 
    threadId = null,
    channel = "api",
    metadata = {}
) {
    try {
        const response = await fetch(\`\${BASE_URL}/external/v1/chat\`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': \`Bearer \${API_KEY}\`
            },
            body: JSON.stringify({
                agent_name: AGENT_NAME,        // ${comments.agentName}
                message: message,              // ${comments.message}
                end_user_id: endUserId,        // ${comments.endUserId}
                channel: channel,              // ${comments.channel}
                thread_id: threadId,           // ${comments.threadId}
                metadata: metadata             // ${comments.metadata}
            })
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else if (response.status === 401) {
            console.error('❌ Erro de autenticação - Verifique sua API Key');
        } else if (response.status === 403) {
            console.error('❌ Acesso negado - Verifique permissões do agente');
        } else if (response.status === 429) {
            console.error('❌ Limite de requisições excedido');
        } else {
            console.error('❌ Erro:', response.status, await response.text());
        }
        
        return null;
    } catch (error) {
        console.error('❌ Erro de rede:', error);
        return null;
    }
}

// 🚀 Exemplo de uso
async function exemploDeUso() {
    // Nova conversa
    console.log('🤖 Iniciando nova conversa...');
    const result = await chatWithAgent(
        "Olá! Como você pode me ajudar?",
        "user_whatsapp_123",
        null,
        "whatsapp",
        {
            source: "site",
            campaign_id: "promo_2024"
        }
    );
    
    if (result) {
        console.log('🤖 Agente:', result.reply);
        const threadId = result.thread_id;
        console.log('🔗 Thread ID:', threadId);
        
        // Continuar conversa
        console.log('\\n🔄 Continuando conversa...');
        const followup = await chatWithAgent(
            "Me conte mais detalhes",
            "user_whatsapp_123",
            threadId,  // Usar o mesmo thread_id
            "whatsapp"
        );
        
        if (followup) {
            console.log('🤖 Agente:', followup.reply);
        }
    } else {
        console.log('❌ Falha na comunicação com o agente');
    }
}

// Executar exemplo
exemploDeUso();`;

    // ⚡ FUNÇÃO JAVASCRIPT STREAMING  
    const generateStreamingJavaScriptCode = () => `/**
 * ⚡ STREAMING MODE - Cliente JavaScript para API Externa
 * 📋 API KEY CONFIGURADA: ${keyDisplayName}
 * 🔑 API KEY GERADA: ${dynamicApiKey}
 * 
 * ✨ MODO STREAMING: Agente configurado com streaming_enabled=true
 * 📡 Usa ReadableStream API para processar eventos SSE em tempo real
 */

const API_KEY = "${dynamicApiKey}";  // ⚠️ SUBSTITUA pela sua API Key REAL!
const BASE_URL = "${baseUrl}";
const AGENT_NAME = "${agentName}";

/**
 * ⚡ Streaming: Envia mensagem e processa resposta em tempo real
 */
async function chatWithAgentStreaming(
    message, 
    endUserId = "user_123", 
    threadId = null,
    channel = "api",
    metadata = {},
    onToken = null,        // Callback para cada token: (token) => {}
    onComplete = null      // Callback para resposta final: (data) => {}
) {
    try {
        const response = await fetch(\`\${BASE_URL}/external/v1/chat\`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': \`Bearer \${API_KEY}\`,
                'Accept': 'text/event-stream'
            },
            body: JSON.stringify({
                agent_name: AGENT_NAME,
                message: message,
                end_user_id: endUserId,
                channel: channel,
                thread_id: threadId,
                metadata: metadata
            })
        });

        if (!response.ok) {
            throw new Error(\`HTTP \${response.status}: \${response.statusText}\`);
        }

        // 📖 Processar stream de eventos SSE
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = "";

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const eventData = JSON.parse(line.substring(6));
                        
                        if (eventData.type === 'token') {
                            // 📡 Token em tempo real
                            const token = eventData.data.token;
                            accumulatedText += token;
                            
                            if (onToken) {
                                onToken(token, accumulatedText);
                            }
                            
                        } else if (eventData.type === 'complete') {
                            // 🏁 Resposta final
                            if (onComplete) {
                                onComplete(eventData.data);
                            }
                            return eventData.data;
                            
                        } else if (eventData.type === 'error') {
                            throw new Error(eventData.data.message);
                        }
                    } catch (parseError) {
                        console.warn('⚠️ Erro ao parsear evento:', line);
                    }
                }
            }
        }
        
    } catch (error) {
        console.error('❌ Erro no streaming:', error);
        throw error;
    }
}

// 🚀 Exemplo de uso do streaming
async function exemploStreamingCompleto() {
    console.log('⚡ Iniciando chat com streaming...');
    
    let textoCompleto = "";
    
    const resultado = await chatWithAgentStreaming(
        "Explique como funciona IA em 3 parágrafos",
        "user_demo_123",
        null,
        "api",
        { source: "exemplo_streaming" },
        
        // 📡 Callback para cada token
        (token, textoAcumulado) => {
            console.log(\`Token: "\${token}"\`);
            console.log(\`Texto até agora: "\${textoAcumulado}"\`);
            
            // Atualizar interface em tempo real
            // document.getElementById('resposta').innerText = textoAcumulado;
        },
        
        // 🏁 Callback para resposta final
        (dadosFinais) => {
            console.log('✅ Streaming completo!');
            console.log('📊 Tokens usados:', dadosFinais.tokens_used);
            console.log('💰 Créditos cobrados:', dadosFinais.credits_charged);
            console.log('🔗 Thread ID:', dadosFinais.thread_id);
        }
    );
    
    console.log('🎉 Chat finalizado:', resultado);
}

// Executar exemplo
exemploStreamingCompleto();`;

    const codes = {
      curl: generateCurlCode(),
      javascript: generateJavaScriptCode(),
    };

    setGeneratedCode(codes);
    
    // Não abrir automaticamente para permitir ambos drawers abertos
    // O usuário pode abrir manualmente via botão no header
  };

  useEffect(() => {
    if (open && selectedApiKeys.length > 0 && agentName && formData) {
      generateCode().catch(error => {
        console.error('Erro ao gerar código:', error);
      });
    }
  }, [open, selectedApiKeys, agentName, codeLanguage, formData?.streaming_enabled]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode[codeLanguage] || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
      ModalProps={{
        keepMounted: true, // Melhor performance em mobile
      }}
      transitionDuration={300}
    >
      {/* Header do Drawer */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApiIcon />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'inherit' }}>
              Gerador de Código
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Integração com {agentName}
            </Typography>
          </Box>
          
          {/* 🆕 INDICADOR DE MODO */}
          <Chip
            size="small"
            label={formData?.streaming_enabled ? "⚡ Streaming" : "🔄 Tradicional"}
            sx={{
              backgroundColor: formData?.streaming_enabled ? 'success.main' : 'primary.main',
              color: 'white',
              fontWeight: 600
            }}
          />
          
          <Tooltip title={
            formData?.streaming_enabled 
              ? "Este agente usa streaming - código gerado com eventos SSE"
              : "Este agente usa modo tradicional - código gerado com requests JSON"
          }>
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Abrir documentação">
            <IconButton 
              onClick={() => setDocumentationOpen(true)}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            >
              <DocsIcon />
            </IconButton>
          </Tooltip>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo do Drawer */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        p: 3,
        overflow: 'hidden'
      }}>
        {/* Configurações */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={{ maxWidth: '100%', overflow: 'hidden', minWidth: 0 }}>
              <InputLabel>API Keys</InputLabel>
              <Select
                multiple
                value={selectedApiKeys}
                onChange={async (e) => {
                  const newSelection = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setSelectedApiKeys(newSelection);
                  
                  // Sincronizar com banco de dados se temos nome do agente
                  if (agentName && agentName.trim()) {
                    await syncApiKeysWithDatabase(newSelection, agentName, availableApiKeys);
                  }
                }}
                label="API Keys"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxWidth: '350px',
                      '& .MuiMenuItem-root': {
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }
                    }
                  }
                }}
                renderValue={(selected) => (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 0.5,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    minWidth: 0,
                    flex: 1
                  }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Selecione API Keys
                      </Typography>
                    ) : selected.length === 1 ? (
                      <Chip
                        label={availableApiKeys.find(key => key.id === selected[0])?.key_name || 'API Key'}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: 'auto',
                          fontSize: '0.75rem',
                          maxWidth: '150px',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    ) : (
                      <Chip
                        label={`${selected.length} selecionadas`}
                        size="small"
                        variant="filled"
                        color="primary"
                        sx={{ height: 'auto', fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              >
                {availableApiKeys.map((key) => (
                  <MenuItem 
                    key={key.id} 
                    value={key.id}
                    sx={{ 
                      maxWidth: '100%',
                      overflow: 'hidden',
                      '& .MuiBox-root': {
                        maxWidth: '100%',
                        minWidth: 0
                      }
                    }}
                  >
                    <Checkbox
                      checked={selectedApiKeys.indexOf(key.id) > -1}
                      sx={{ mr: 1, flexShrink: 0 }}
                    />
                    <Box sx={{ 
                      minWidth: 0,
                      overflow: 'hidden',
                      width: '100%'
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {key.key_name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block'
                        }}
                      >
                        {key.key_preview}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={{ maxWidth: '100%', overflow: 'hidden', minWidth: 0 }}>
              <InputLabel>Linguagem</InputLabel>
              <Select
                value={codeLanguage}
                onChange={(e) => setCodeLanguage(e.target.value)}
                label="Linguagem"
              >
                {codeLanguages.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {        /* Área do Código */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          <Paper 
            elevation={1} 
            sx={{ 
              flex: 1,
              p: 2, 
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.300',
              overflow: 'auto',
              position: 'relative',
              maxHeight: 'calc(100vh - 300px)' // Garantir scroll
            }}
          >
            <pre style={{ 
              margin: 0, 
              fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap'
            }}>
              {generatedCode[codeLanguage] || 'Gerando código...'}
            </pre>
            
            {/* Botão de copiar flutuante */}
            <Tooltip title={copied ? "Copiado!" : "Copiar código"}>
              <IconButton
                onClick={copyToClipboard}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>

        {/* Alertas informativos */}
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              <strong>📖 Documentação:</strong> Para mais detalhes sobre a API Externa, acesse: 
              <code style={{ marginLeft: 8 }}>{process.env.REACT_APP_EXTERNAL_API_BASE_URL || 'http://localhost:8000'}/external-docs/docs</code>
            </Typography>
          </Alert>

          <Alert severity="warning" variant="outlined">
            <Typography variant="body2">
              <strong>🔐 Segurança:</strong> Mantenha sua API Key segura. Nunca a compartilhe publicamente ou a inclua em repositórios versionados.
            </Typography>
          </Alert>
        </Stack>

        {/* Botão de ação principal */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
          <Button 
            variant="contained" 
            startIcon={<CopyIcon />}
            onClick={copyToClipboard}
          >
            {copied ? 'Copiado!' : 'Copiar Código'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

// Função helper para sincronizar API Keys selecionadas com o banco de dados
const syncApiKeysWithDatabase = async (selectedKeys, agentName, availableApiKeys) => {
  try {
    console.log('🔄 [SYNC] Sincronizando API Keys com banco de dados...');
    console.log('🔄 [SYNC] API Keys selecionadas:', selectedKeys);
    console.log('🔄 [SYNC] Nome do agente:', agentName);

    // Para cada API Key disponível, atualizar allowed_agents
    for (const apiKey of availableApiKeys) {
      const shouldIncludeAgent = selectedKeys.includes(apiKey.id);
      let updatedAllowedAgents = [...(apiKey.allowed_agents || [])];

      if (shouldIncludeAgent) {
        // Adicionar agente se não estiver na lista
        if (!updatedAllowedAgents.includes(agentName)) {
          updatedAllowedAgents.push(agentName);
          console.log(`✅ [SYNC] Adicionando '${agentName}' à API Key '${apiKey.key_name}'`);
        }
      } else {
        // Remover agente se estiver na lista
        updatedAllowedAgents = updatedAllowedAgents.filter(agent => agent !== agentName);
        console.log(`❌ [SYNC] Removendo '${agentName}' da API Key '${apiKey.key_name}'`);
      }

      // Atualizar apenas se houve mudança
      if (JSON.stringify(updatedAllowedAgents.sort()) !== JSON.stringify((apiKey.allowed_agents || []).sort())) {
        try {
          const response = await externalApiService.updateApiKey(apiKey.id, {
            allowed_agents: updatedAllowedAgents
          });
          console.log(`🔄 [SYNC] API Key '${apiKey.key_name}' atualizada:`, response);
        } catch (error) {
          console.error(`❌ [SYNC] Erro ao atualizar API Key '${apiKey.key_name}':`, error);
        }
      }
    }

    console.log('✅ [SYNC] Sincronização concluída!');
  } catch (error) {
    console.error('❌ [SYNC] Erro na sincronização:', error);
  }
};

const CreateAgent = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const isEditMode = Boolean(agentId);

  // Estados principais
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    streaming_enabled: false, // 🆕 Configuração de streaming
    message_delay_seconds: 0.0, // 🆕 Delay para mensagens múltiplas
    output_type_config: null, // 🆕 Schema para structured output
    llm_config: {
      model: '', // Será definido após carregar o modelo padrão
      temperature: 0.7,
      max_tokens: 1500,
      top_p: 0.9,
      presence_penalty: 0.1,
      frequency_penalty: 0.1,
    },
    tools_config: [],
    handoffs_config: [],
    guardrails_config: {
      input_guardrails: [],
      output_guardrails: [],
    },
    is_active: true,
  });

  // Estados para controle de alterações não salvas
  const [originalFormData, setOriginalFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [toolsAutoSaved, setToolsAutoSaved] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false); // Flag para saber se dados foram carregados

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [currentTab, setCurrentTab] = useState('config');
  const [availableTools, setAvailableTools] = useState([]);
  const [agentIntegrations, setAgentIntegrations] = useState([]);

  // Estados para teste do agente
  const [testMode, setTestMode] = useState(false);
  const [testMessages, setTestMessages] = useState([]);

  // Estados para trace do agente
  const [traceMode, setTraceMode] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  
  // Estados do Gerador de Código
  const [codeGeneratorOpen, setCodeGeneratorOpen] = useState(false);
  const [documentationOpen, setDocumentationOpen] = useState(false);
  const [availableApiKeys, setAvailableApiKeys] = useState([]);
  
  // 🆕 Estados para Structured Output
  const [outputFormat, setOutputFormat] = useState('text'); // 'text' ou 'structured'
  const [schemaEditorOpen, setSchemaEditorOpen] = useState(false);
  const [selectedApiKeys, setSelectedApiKeys] = useState([]);
  const [generatedCode, setGeneratedCode] = useState({});
  const [codeLanguage, setCodeLanguage] = useState('curl');
  const [companyInfo, setCompanyInfo] = useState(null);

  // Estados para bases de conhecimento
  const [agentKnowledgeBases, setAgentKnowledgeBases] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbError, setKbError] = useState(null);
  
  // 🚀 Estados para dialog de adicionar bases
  const [addKbDialogOpen, setAddKbDialogOpen] = useState(false);
  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState([]);
  const [loadingAvailableBases, setLoadingAvailableBases] = useState(false);
  const [addingKnowledgeBase, setAddingKnowledgeBase] = useState(false);

  // 🆕 Estados para Data Collection (lazy loading)
  const [hasDataCollection, setHasDataCollection] = useState(false);
  const [dataCollectionLoading, setDataCollectionLoading] = useState(false);
  const initialLoadAgentIdRef = useRef(null);


  // 🚀 FASE 1: Carregar dados iniciais em paralelo
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('🚀 [OTIMIZAÇÃO] Iniciando carregamentos paralelos...');
        const startTime = Date.now();

        // Definir carregamentos independentes que podem ser paralelos
        const independentPromises = [
          loadAvailableTools(),
          loadAvailableModels(),
          loadCompanyInfo()
        ];

        // Se estiver em modo de edição, adicionar carregamento do agente
        if (isEditMode) {
          independentPromises.push(loadAgentData());
        }

        // Executar todos os carregamentos em paralelo
        await Promise.all(independentPromises);

        const endTime = Date.now();
        console.log(`✅ [OTIMIZAÇÃO] Carregamentos paralelos concluídos em ${endTime - startTime}ms`);
      } catch (error) {
        console.error('❌ [OTIMIZAÇÃO] Erro nos carregamentos paralelos:', error);
        setError('Erro ao carregar dados iniciais: ' + error.message);
      }
    };

    // Guard: evita chamadas duplicadas por agentId
    if (initialLoadAgentIdRef.current !== agentId) {
      initialLoadAgentIdRef.current = agentId;
      loadInitialData();
    }
  }, [agentId]);

  // Recarregar API Keys quando o nome do agente mudar (com debounce para evitar requisições excessivas)
  useEffect(() => {
    // ⚠️ PROBLEMA: Esta função estava sendo chamada a cada caractere digitado!
    // ✅ SOLUÇÃO: Adicionar debounce de 500ms
    
    const timeoutId = setTimeout(() => {
      if (formData.name && formData.name.trim().length >= 3) {
        // Só carregar API Keys se o nome tiver pelo menos 3 caracteres
        loadApiKeys();
      } else if (!formData.name || formData.name.trim().length === 0) {
        // Limpar API Keys se nome estiver vazio
        setAvailableApiKeys([]);
        setSelectedApiKeys([]);
      }
    }, 500); // Aguardar 500ms após parar de digitar

    return () => clearTimeout(timeoutId);
  }, [formData.name]);

  // Carregar API Keys quando o gerador de código for aberto
  useEffect(() => {
    if (codeGeneratorOpen && formData.name) {
      console.log('🔍 Gerador de código aberto, carregando API Keys para agente:', formData.name);
      loadApiKeys();
    }
  }, [codeGeneratorOpen]);
  
  // 🆕 useEffect para detectar structured output ao carregar agente
  useEffect(() => {
    if (formData.output_type_config && Object.keys(formData.output_type_config).length > 0) {
      setOutputFormat('structured');
      // ⚠️ Desabilitar streaming automaticamente (incompatível com structured output)
      // Só modificar se dados já foram carregados (evita mudanças durante carregamento inicial)
      if (formData.streaming_enabled && dataLoaded) {
        const updatedFormData = {
          ...formData,
          streaming_enabled: false
        };
        setFormData(updatedFormData);
        // Atualizar originalFormData para evitar falso positivo de mudanças
        if (originalFormData) {
          setOriginalFormData(JSON.stringify(updatedFormData));
        }
        console.log('⚠️ [STRUCTURED OUTPUT] Streaming desabilitado automaticamente (incompatível com JSON estruturado)');
      }
    } else {
      setOutputFormat('text');
    }
  }, [formData.output_type_config, dataLoaded]);
  
  // 🆕 Handlers para Structured Output
  const handleSaveSchema = (schema) => {
    const updatedFormData = {
      ...formData,
      output_type_config: schema,
      streaming_enabled: false // ⚠️ Desabilitar streaming (incompatível)
    };
    setFormData(updatedFormData);
    setOutputFormat('structured');
    // Atualizar originalFormData se já foi carregado (evita "unsaved changes" ao salvar schema)
    if (dataLoaded && originalFormData) {
      setOriginalFormData(JSON.stringify(updatedFormData));
    }
    console.log('📦 [STRUCTURED OUTPUT] Schema salvo:', schema);
    console.log('⚠️ [STRUCTURED OUTPUT] Streaming desabilitado automaticamente');
  };
  
  const handleRemoveSchema = () => {
    setFormData(prev => ({
      ...prev,
      output_type_config: null
    }));
    setOutputFormat('text');
    console.log('🗑️ [STRUCTURED OUTPUT] Schema removido');
  };



  // Carregar TODAS as API Keys da empresa (não filtradas por agente)
  const loadApiKeys = async () => {
    try {
      console.log('🔍 [FRONTEND] loadApiKeys chamado - carregando todas as API Keys da empresa');
      
      // Se não temos um nome de agente, não carregar API Keys
      if (!formData.name) {
        console.log('⚠️ [FRONTEND] Nome do agente vazio, limpando API Keys');
        setAvailableApiKeys([]);
        setSelectedApiKeys([]);
        return;
      }

      console.log('📡 [FRONTEND] Fazendo requisição para buscar TODAS as API Keys da empresa');
      // Buscar TODAS as API Keys da empresa (não filtradas por agente)
      const response = await externalApiService.getApiKeys({ 
        include_inactive: false 
      });
      
      console.log('📨 [FRONTEND] Resposta recebida:', response);
      console.log('🔍 [FRONTEND] Resposta completa (JSON):', JSON.stringify(response, null, 2));
      
      const apiKeys = response.items || response.api_keys || [];
      console.log('📋 [FRONTEND] API Keys encontradas:', apiKeys.length);
      console.log('📋 [FRONTEND] API Keys detalhes:', apiKeys);
      
      setAvailableApiKeys(apiKeys);
      
      // Pré-selecionar API Keys que já permitem acesso a este agente
      if (formData.name && formData.name.trim()) {
        const agentName = formData.name.trim();
        const preSelectedKeys = apiKeys
          .filter(key => key.allowed_agents && key.allowed_agents.includes(agentName))
          .map(key => key.id);
        
        if (preSelectedKeys.length > 0) {
          console.log('🔄 [FRONTEND] Pré-selecionando API Keys que já permitem este agente:', preSelectedKeys);
          setSelectedApiKeys(preSelectedKeys);
        }
      }
      
      console.log('📋 [FRONTEND] API Keys carregadas!');
    } catch (error) {
      console.error('❌ [FRONTEND] Erro ao carregar API Keys da empresa:', error);
      setAvailableApiKeys([]);
      setSelectedApiKeys([]);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const result = await authService.getCompanyInfo();
      setCompanyInfo(result);
    } catch (error) {
      console.error('Erro ao carregar informações da empresa:', error);
      // Fallback para dados padrão se não conseguir buscar
      setCompanyInfo({
        company: { name: "Sua Empresa" },
        rate_limits: {
          tier_name: "basic",
          display_name: "Básico",
          requests_per_minute: 60,
          requests_per_hour: 1000,
          requests_per_day: 10000,
          burst_allowance: 100
        }
      });
    }
  };

  // Definir título da página
  useEffect(() => {
    const pageTitle = isEditMode 
      ? `Editando: ${formData.name || 'Agente'} - organIA`
      : 'Novo Agente - organIA';
    document.title = pageTitle;
    
    // Cleanup - voltar título padrão quando sair da página
    return () => {
      document.title = 'organIA - Agentes Inteligentes';
    };
  }, [isEditMode, formData.name]);

  // Armazenar os dados originais para comparação
  useEffect(() => {
    if (dataLoaded && !originalFormData) {
      setOriginalFormData(JSON.stringify(formData));
      console.log('📝 [UNSAVED CHANGES] Dados originais capturados');
    }
  }, [dataLoaded]);
  // Conversas recentes do agente (somente edição)
  useEffect(() => {
    const loadRecentSessions = async () => {
      try {
        if (!agentId) return;
        const res = await agentsService.getSessionsSummary(agentId, 5);
        setRecentSessions(res?.sessions || []);
      } catch (e) {
        // silencioso
      }
    };
    loadRecentSessions();
  }, [agentId]);

  // Detectar alterações não salvas
  useEffect(() => {
    if (originalFormData && !loading) {
      // Criar uma cópia do formData atual para comparação
      const formDataForComparison = { ...formData };
      const originalFormDataObj = JSON.parse(originalFormData);

      // Remover tools_config de ambos os objetos para comparação
      // já que as ferramentas são salvas automaticamente
      delete formDataForComparison.tools_config;
      const originalWithoutTools = { ...originalFormDataObj };
      delete originalWithoutTools.tools_config;

      // Ordenar chaves para comparação consistente
      const sortKeys = (obj) => {
        return Object.keys(obj).sort().reduce((acc, key) => {
          acc[key] = obj[key];
          return acc;
        }, {});
      };

      // Comparar os objetos sem tools_config
      const currentFormDataStr = JSON.stringify(sortKeys(formDataForComparison));
      const originalWithoutToolsStr = JSON.stringify(sortKeys(originalWithoutTools));

      const hasChanges = currentFormDataStr !== originalWithoutToolsStr;

      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, originalFormData, loading]);

  // Ouvir eventos de salvamento automático de ferramentas
  useEffect(() => {
    const handleToolConfigSaved = async (event) => {
      setToolsAutoSaved(true);
      // Exibir notificação temporária
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      // ✅ SIMPLIFICADO: updateToolConfigs() já fez toda sincronização
      // Apenas recarregar dados para manter interface sincronizada
      if (isEditMode && agentId) {
        try {
          console.log('🔄 Recarregando dados após configuração de ferramenta...');
          
          // Recarregar integrações para UI
          const updatedIntegrations = await integrationsService.getAgentIntegrations(agentId);
          setAgentIntegrations(updatedIntegrations);

          // Recarregar agente completo para obter tools_config atualizado
          const updatedAgent = await agentsService.getAgent(agentId);
          
          // Atualizar formData com dados mais recentes do banco
          const updatedFormData = {
            ...formData,
            tools_config: updatedAgent.tools_config || [],
            output_type_config: updatedAgent.output_type_config || null, // 🆕 Preservar schema
          };
          setFormData(updatedFormData);

          // Atualizar originalFormData para evitar "unsaved changes"
          setOriginalFormData(JSON.stringify(updatedFormData));
          
          console.log('✅ Dados recarregados após configuração de ferramenta');

        } catch (err) {
          console.error('Erro ao recarregar dados após configuração de ferramenta:', err);
        }
      } else if (event.detail?.tools_config) {
        // Para novos agentes, usar as ferramentas do evento (legado)
        const updatedFormData = {
          ...formData,
          tools_config: event.detail.tools_config
        };
        setFormData(updatedFormData);

        // Atualizar o estado original para evitar detecção de alterações não salvas
        const originalFormDataObj = originalFormData ? JSON.parse(originalFormData) : {};
        const updatedOriginalFormData = {
          ...originalFormDataObj,
          tools_config: event.detail.tools_config
        };
        setOriginalFormData(JSON.stringify(updatedOriginalFormData));
      }
    };

    window.addEventListener('toolConfigSaved', handleToolConfigSaved);
    return () => window.removeEventListener('toolConfigSaved', handleToolConfigSaved);
  }, [formData, agentId, isEditMode]);

  // Confirmar antes de sair da página e adicionar atalhos de teclado
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        const message = "Você tem alterações não salvas. Deseja realmente sair?";
        e.returnValue = message;
        return message;
      }
    };

    // Interceptar navegação dentro do aplicativo
    const handleCheckUnsavedChanges = (e) => {
      if (hasUnsavedChanges) {
        // Impedir a navegação padrão
        e.preventDefault();

        // Mostrar diálogo de confirmação
        setPendingNavigation(e.detail.path);
        setShowUnsavedDialog(true);
        return false;
      }
      return true;
    };

    // Atalho de teclado para abrir/fechar teste
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + T para toggle do teste (apenas se agente salvo)
      if ((e.ctrlKey || e.metaKey) && e.key === 't' && isEditMode) {
        e.preventDefault();
        setTestMode(prev => !prev);
      }
      // Ctrl/Cmd + S para salvar
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!loading) {
          handleSave();
        }
      }
      // Escape para fechar teste
      if (e.key === 'Escape' && testMode) {
        setTestMode(false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('checkUnsavedChanges', handleCheckUnsavedChanges);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('checkUnsavedChanges', handleCheckUnsavedChanges);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasUnsavedChanges, isEditMode, testMode]);

  const loadAgentData = async () => {
    try {
      console.log('🚀 [OTIMIZAÇÃO] Carregando agente primeiro (render rápido)...');
      const t0 = Date.now();

      // Fase A: carregar apenas o agente e renderizar imediatamente
      const agent = await agentsService.getAgent(agentId);
      setFormData({
        name: agent.name,
        description: agent.description,
        instructions: agent.instructions,
        streaming_enabled: agent.streaming_enabled || false,
        message_delay_seconds: agent.message_delay_seconds || 0.0,
        llm_config: agent.llm_config,
        tools_config: agent.tools_config,
        handoffs_config: agent.handoffs_config,
        guardrails_config: agent.guardrails_config,
        is_active: agent.is_active,
        output_type_config: agent.output_type_config || null, // 🆕 Carregar schema estruturado
      });
      setDataLoaded(true); // 🔧 Marcar que dados foram carregados do backend
      console.log(`✅ [OTIMIZAÇÃO] Agent básico em ${Date.now() - t0}ms`);
      
      // 🆕 Detectar se tem output estruturado configurado
      if (agent.output_type_config) {
        console.log('📦 [STRUCTURED OUTPUT] Schema detectado no banco:', agent.output_type_config);
      }

      // Set loaders por seção
      setKbLoading(true);
      
      // Fase B: carregar integrações e KBs em paralelo, sem bloquear a UI
      const [integrations, knowledgeBases] = await Promise.all([
        integrationsService.getAgentIntegrations(agentId),
        ragService.getAgentActiveKnowledgeBases(agentId)
      ]);

      setAgentIntegrations(integrations);
      const compatibleKnowledgeBases = {
        ...knowledgeBases,
        available_bases: (knowledgeBases.active_bases || []).map(kb => ({
          ...kb,
          is_enabled: true
        }))
      };
      setAgentKnowledgeBases(compatibleKnowledgeBases);
      
      // 🆕 Verificação leve: agente tem data collection? (sem carregar dados)
      const hasDataCollectionTool = integrations.some(
        integration => integration.integration_type === 'data_collection' && integration.is_active
      );
      setHasDataCollection(hasDataCollectionTool);
      
      console.log(`✅ [OTIMIZADO] Bases de conhecimento carregadas: ${knowledgeBases.total_active} ativas (otimização: 85% menos dados)`);
      console.log('🔍 [DATA_COLLECTION] Agente tem ferramenta de coleta:', hasDataCollectionTool);
    } catch (err) {
      console.error('❌ [OTIMIZAÇÃO] Erro ao carregar dados do agente:', err);
      setError('Erro ao carregar dados do agente: ' + err.message);
    } finally {
      setKbLoading(false);
    }
  };

  const loadAvailableTools = async () => {
    try {
      const tools = await integrationsService.getAvailableTools();
      setAvailableTools(tools.tools);
    } catch (err) {
      console.error('Erro ao carregar ferramentas:', err);
    }
  };

  const loadAvailableModels = async () => {
    try {
      // 🎯 PHASE 5: Usar nova função com filtro LLM
      const models = await agentsService.getAvailableModelsByType('llm');
      setAvailableModels(models);
      
      // Se não estiver em modo de edição, definir modelo padrão
      if (!isEditMode && !formData.llm_config.model) {
        const defaultModel = await agentsService.getDefaultModel('llm');
        setFormData(prev => ({
          ...prev,
          llm_config: {
            ...prev.llm_config,
            model: defaultModel
          }
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar modelos LLM:', err);
      // Fallback para modelos LLM padrão
      setAvailableModels([
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
        { value: 'gpt-4o', label: 'GPT-4o' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
      ]);
      
      // Definir fallback como padrão se não estiver em modo de edição
      if (!isEditMode && !formData.llm_config.model) {
        setFormData(prev => ({
          ...prev,
          llm_config: {
            ...prev.llm_config,
            model: 'gpt-4o-mini' // Fallback se API falhar
          }
        }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Removemos a função syncToolsConfig, pois a sincronização deve ser feita apenas
  // nas dialogs de ferramentas e nos cards das ferramentas, não ao salvar o agente

  const handleSave = async (showSuccess = true) => {
    try {
      setLoading(true);
      setError(null);

      // IMPORTANTE: Não alterar tools_config ao salvar o agente
      // Remover tools_config do formData para evitar que o backend sobrescreva
      // as ferramentas que já estão configuradas
      const formDataWithoutTools = { ...formData };
      delete formDataWithoutTools.tools_config;

      if (isEditMode) {
        // Usar formDataWithoutTools para não enviar tools_config
        await agentsService.updateAgent(agentId, formDataWithoutTools);

        // Atualizar o estado do formulário com os dados atuais
        // Importante: Manter o tools_config atual, não o que veio do backend
        setOriginalFormData(JSON.stringify(formData));
      } else {
        // Para novos agentes, também não enviar tools_config
        const newAgent = await agentsService.createAgent(formDataWithoutTools);
        navigate(`/agents/${newAgent.id}/edit`);
        return newAgent.id; // Retornar o ID para auto-save
      }

      if (showSuccess) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      setError('Erro ao salvar agente: ' + err.message);
      throw err; // Re-throw para auto-save tratar
    } finally {
      setLoading(false);
    }
  };

  // Função para auto-save quando necessário (ex: configurar ferramentas)
  const handleAutoSave = async () => {
    // Validar campos obrigatórios
    if (!formData.name.trim()) {
      throw new Error('Nome do agente é obrigatório');
    }
    if (!formData.instructions.trim()) {
      throw new Error('Comportamento do agente é obrigatório');
    }

    return await handleSave(false); // Não mostrar mensagem de sucesso
  };

  // 🚀 FASE 2: Função para lidar com mudanças de bases de conhecimento
  // Mantém o salvamento independente via ragService
  const handleKnowledgeBasesChange = async (updatedKBs) => {
    try {
      console.log('🚀 [FASE 2] Atualizando bases de conhecimento...', updatedKBs?.length || 0);
      setAgentKnowledgeBases(updatedKBs);
      
      // 🔒 CRÍTICO: O salvamento real continua sendo feito no ToolsConfiguration
      // via ragService.toggleAgentKnowledgeBase() - NÃO MEXER NESSA LÓGICA
      
    } catch (error) {
      console.error('❌ [FASE 2] Erro ao atualizar bases de conhecimento:', error);
      setKbError('Erro ao atualizar bases de conhecimento: ' + error.message);
    }
  };

  // 🚀 FASE 2: Função para limpar erros de bases de conhecimento
  const handleClearKbError = () => {
    setKbError(null);
  };

  const handleTestAgent = async (message, threadId = null, withTrace = false, mockResponse = null) => {
    try {
        let response;
        
        // 🆕 Se é apenas mensagem do usuário, adicionar apenas ela
        if (mockResponse && mockResponse.type === 'user_only') {
          setTestMessages(prev => [
            ...prev,
            { type: 'user', content: message, timestamp: new Date() }
          ]);
          return; // Não processar resposta ainda
        }
        
        // 🆕 Se há mockResponse (vindo do streaming ou WebSocket), usar diretamente
        if (mockResponse) {
          response = mockResponse;
        } else {
          // 🔄 USAR HTTP TRADICIONAL (WebSocket é gerenciado pelo AgentTester)
          response = withTrace 
            ? await agentsService.testAgentWithTrace(agentId, message, threadId)
            : await agentsService.testAgent(agentId, message, threadId);
        }
      
      // 🆕 Se é WebSocket, não adicionar mensagem do usuário (já foi adicionada)
      if (mockResponse && mockResponse.websocket) {
        setTestMessages(prev => [
          ...prev,
          { type: 'agent', content: response.answer, timestamp: new Date() }
        ]);
      } else {
        // 🔍 VERIFICAR SE É RESPOSTA DO BUFFER HTTP
        if (response.is_buffered && response.buffer_messages > 1) {
          // ✅ BUFFER HTTP: Usar functional update para evitar stale closure
          const bufferResponseId = `${response.thread_id}_buffer_${response.buffer_messages}`;
          
          // ✅ SOLUÇÃO DEFINITIVA: Usar setState funcional para verificar duplicata
          setTestMessages(prev => {
            // Verificar duplicata com estado ATUAL
            const isDuplicateBufferResponse = prev.some(msg => 
              msg.type === 'agent' && 
              msg.buffer_response_id === bufferResponseId
            );
            
            if (!isDuplicateBufferResponse) {
              // Primeira resposta do buffer: adicionar resposta do agente
              return [
                ...prev,
                { 
                  type: 'agent', 
                  content: response.answer, 
                  timestamp: new Date(),
                  buffer_info: `${response.buffer_messages} mensagens combinadas`,
                  buffer_response_id: bufferResponseId  // ✅ ID único para detectar duplicatas
                }
              ];
            } else {
              // Resposta duplicada do buffer: ignorar
              return prev;
            }
          });
        } else {
          // HTTP tradicional: adicionar apenas resposta do agente (usuário já foi adicionado via user_only)
          setTestMessages(prev => [
            ...prev,
            { type: 'agent', content: response.answer, timestamp: new Date() }
          ]);
        }
      }

      // 🆕 Se há trace na resposta, processar APENAS uma vez
      if (response.trace && !mockResponse) {  // 🔧 Evitar duplicação quando vem do streaming
        console.log('🔍 [CREATE_AGENT] Processando trace data:', response.trace);
        handleTraceData(response.trace);
      }

      // Retornar response para que o AgentTester possa extrair o thread_id
      return response;
    } catch (err) {
      setTestMessages(prev => [
        ...prev,
        { type: 'user', content: message, timestamp: new Date() },
        { type: 'error', content: 'Erro: ' + err.message, timestamp: new Date() },
      ]);

      // Retornar erro para que o AgentTester possa tratar
      throw err;
    }
  };

  const handleNewConversation = () => {
    setTestMessages([]);
  };

  const handleTraceData = (data) => {
    console.log('🔍 [HANDLE_TRACE_DATA] Recebendo trace:', data);
    
    // 🔧 Validar se é um trace válido ou o response completo
    let traceToSet = data;
    if (data && data.trace && data.answer) {
      // É o response completo, extrair apenas o trace
      console.log('🔧 [HANDLE_TRACE_DATA] Detectado response completo, extraindo trace...');
      traceToSet = data.trace;
    }
    
    console.log('🔍 [HANDLE_TRACE_DATA] Trace final a ser usado:', traceToSet);
    setTraceData(traceToSet);
    setTraceMode(true); // Abrir drawer automaticamente quando trace for recebido
    console.log('🔍 [HANDLE_TRACE_DATA] TraceDrawer deveria abrir agora');
  };

  const tabs = [
    { id: 'config', label: 'Configuração', icon: <SettingsIcon fontSize="small" /> },
    ...(isEditMode ? [{ id: 'sessions', label: 'Conversas', icon: <ChatIcon fontSize="small" /> }] : []),
    ...(isEditMode ? [{ id: 'performance', label: 'Diagnóstico', icon: <SpeedIcon fontSize="small" /> }] : []),
    ...(isEditMode && hasDataCollection ? [{ id: 'data_collection', label: 'Coleta de Dados', icon: <FolderIcon fontSize="small" /> }] : [])
  ];

  // Estado para modelos disponíveis
  const [availableModels, setAvailableModels] = useState([]);

  // Função para lidar com a navegação quando há alterações não salvas
  const handleNavigation = (path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  };

  // Função para salvar e navegar
  const handleSaveAndNavigate = async () => {
    try {
      await handleSave(false);
      setShowUnsavedDialog(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    } catch (err) {
      // Erro já tratado em handleSave
    }
  };

  // Função para descartar alterações e navegar
  const handleDiscardAndNavigate = () => {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  // Carregar bases de conhecimento para o agente específico
  // Removido: Carregamento duplicado de KBs ativas
  // O carregamento inicial já é feito em loadAgentData() dentro de loadInitialData()

  const loadAgentKnowledgeBases = async () => {
    if (!agentId) return;
    
    try {
      setKbLoading(true);
      setKbError(null);
      
      // 🚀 OTIMIZADO: Carregar apenas bases ATIVAS (85% menos dados)
      const data = await ragService.getAgentActiveKnowledgeBases(agentId);
      
      // Converter para formato compatível com interface existente
      const compatibleData = {
        ...data,
        available_bases: data.active_bases.map(kb => ({
          ...kb,
          is_enabled: true  // Todas são ativas por definição
        }))
      };
      
      setAgentKnowledgeBases(compatibleData);
      
      console.log(`✅ [OTIMIZADO] Carregadas ${data.total_active} bases ativas (vs todas as bases antes)`);
      
    } catch (err) {
      console.error('Erro ao carregar bases de conhecimento do agente:', err);
      setKbError('Erro ao carregar bases de conhecimento: ' + err.message);
    } finally {
      setKbLoading(false);
    }
  };

  // Funções para gerenciar bases de conhecimento
  const handleKnowledgeBaseToggle = async (kbId, enabled) => {
    if (!agentId) {
      setError('Agente deve estar salvo antes de configurar bases de conhecimento');
      return;
    }

    try {
      setKbLoading(true);
      
      await ragService.toggleAgentKnowledgeBase(agentId, {
        knowledge_base_id: kbId,
        enabled: enabled
      });
      
      // Recarregar dados
      await loadAgentKnowledgeBases();
      
      // Notificar sucesso
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
    } catch (err) {
      console.error('Erro ao alterar base de conhecimento:', err);
      setError('Erro ao alterar base de conhecimento: ' + err.message);
    } finally {
      setKbLoading(false);
    }
  };

  // 🚀 FUNÇÕES DO DIALOG OTIMIZADO
  const handleOpenAddKbDialog = async () => {
    if (!agentId) {
      setError('Agente deve estar salvo antes de adicionar bases de conhecimento');
      return;
    }

    try {
      setLoadingAvailableBases(true);
      setAddKbDialogOpen(true);
      
      // Carregar bases disponíveis usando endpoint otimizado
      const data = await ragService.getAgentAvailableKnowledgeBases(agentId);
      setAvailableKnowledgeBases(data.available_bases || []);
      
      console.log(`📋 [DIALOG] Carregadas ${data.total_available} bases disponíveis para seleção`);
      
    } catch (err) {
      console.error('Erro ao carregar bases disponíveis:', err);
      setKbError('Erro ao carregar bases disponíveis: ' + err.message);
      setAddKbDialogOpen(false);
    } finally {
      setLoadingAvailableBases(false);
    }
  };

  const handleAddKnowledgeBase = async (kbId) => {
    try {
      setAddingKnowledgeBase(true);
      
      // Habilitar a base usando o método existente
      await ragService.toggleAgentKnowledgeBase(agentId, {
        knowledge_base_id: kbId,
        enabled: true
      });
      
      console.log(`✅ [DIALOG] Base de conhecimento ${kbId} habilitada com sucesso`);
      
      // Recarregar bases ativas (otimizado)
      await loadAgentKnowledgeBases();
      
      // Fechar dialog
      setAddKbDialogOpen(false);
      setAvailableKnowledgeBases([]);
      
    } catch (err) {
      console.error('Erro ao adicionar base de conhecimento:', err);
      setKbError('Erro ao adicionar base de conhecimento: ' + err.message);
    } finally {
      setAddingKnowledgeBase(false);
    }
  };

  const handleCloseAddKbDialog = () => {
    setAddKbDialogOpen(false);
    setAvailableKnowledgeBases([]);
    setLoadingAvailableBases(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      maxWidth: '100vw',
      overflow: 'hidden',
      width: '100%'
    }}>
      {/* Diálogo de confirmação para alterações não salvas */}
      <Dialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        aria-labelledby="unsaved-dialog-title"
        aria-describedby="unsaved-dialog-description"
      >
        <DialogTitle id="unsaved-dialog-title">
          Alterações não salvas
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-dialog-description">
            Você tem alterações não salvas no agente.
            {toolsAutoSaved && (
              <Alert severity="info" sx={{ mt: 2 }}>
                As alterações nas ferramentas já foram salvas automaticamente.
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDiscardAndNavigate} color="error">
            Descartar alterações
          </Button>
          <Button onClick={handleSaveAndNavigate} variant="contained" color="primary">
            Salvar e continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toolbar com ações principais */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => {
              if (hasUnsavedChanges) {
                setPendingNavigation('/agents');
                setShowUnsavedDialog(true);
              } else {
                navigate('/agents');
              }
            }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditMode ? `Editando: ${formData.name}${hasUnsavedChanges ? ' *' : ''}` : 'Configurar novo agente'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
            }
            label="Ativo"
          />

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            size="small"
          >
            {loading ? <CircularProgress size={16} /> : 'Salvar'}
          </Button>
        </Box>
      </Box>

      {/* Conteúdo Principal */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '70vh',
        maxWidth: '100%',
        overflow: 'hidden',
      }}>
        {/* Painel de Configuração */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Tabs de Navegação */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Stack direction="row" spacing={3}>
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  startIcon={tab.icon}
                  onClick={() => setCurrentTab(tab.id)}
                  size="small"
                  sx={{
                    color: currentTab === tab.id ? 'primary.main' : 'text.secondary',
                    fontWeight: currentTab === tab.id ? 600 : 400,
                    borderBottom: currentTab === tab.id ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    textTransform: 'none',
                    py: 1,
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Stack>
          </Box>

          {/* Conteúdo das Tabs */}
          <Box sx={{ 
            flexGrow: 1,
          }}>
            {/* Mensagens de Feedback */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Agente salvo com sucesso!
              </Alert>
            )}

            {/* Aba Configuração */}
            {currentTab === 'config' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Stack spacing={2}>
                  {/* Seção Informações Básicas */}
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="basic-content"
                      id="basic-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Informações Básicas
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Nome do Agente"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Ex: Assistente de Vendas"
                          required
                          sx={{ maxWidth: '100%', minWidth: 0 }}
                        />

                        {/* 🔑 Multiselect de API Keys disponíveis */}
                        {formData.name && (
                          <FormControl fullWidth sx={{ maxWidth: '100%', overflow: 'hidden', minWidth: 0 }}>
                            <InputLabel id="api-keys-select-label">API Keys</InputLabel>
                            <Select
                              labelId="api-keys-select-label"
                              multiple
                              value={selectedApiKeys}
                              onChange={async (e) => {
                                const newSelection = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                                setSelectedApiKeys(newSelection);
                                
                                // Sincronizar com banco de dados se temos nome do agente
                                if (formData.name && formData.name.trim()) {
                                  await syncApiKeysWithDatabase(newSelection, formData.name, availableApiKeys);
                                }
                              }}
                              label="API Keys"
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxWidth: '400px',
                                    '& .MuiMenuItem-root': {
                                      maxWidth: '100%',
                                      overflow: 'hidden'
                                    }
                                  }
                                }
                              }}
                              renderValue={(selected) => (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  gap: 0.5,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  minWidth: 0,
                                  flex: 1
                                }}>
                                  {selected.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                      Selecione as API Keys
                                    </Typography>
                                  ) : selected.length === 1 ? (
                                    <Chip
                                      label={availableApiKeys.find(key => key.id === selected[0])?.key_name || 'API Key'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ 
                                        height: 'auto',
                                        maxWidth: '200px',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label={`${selected.length} API Keys selecionadas`}
                                      size="small"
                                      variant="filled"
                                      color="primary"
                                      sx={{ height: 'auto' }}
                                    />
                                  )}
                                </Box>
                              )}
                            >
                              {availableApiKeys.length === 0 ? (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                    <WarningIcon fontSize="small" color="warning" />
                                    <Typography variant="body2" color="text.secondary">
                                      Nenhuma API Key configurada para este agente
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ) : (
                                availableApiKeys.map((apiKey) => (
                                <MenuItem 
                                  key={apiKey.id} 
                                  value={apiKey.id}
                                  sx={{ 
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    '& .MuiBox-root': {
                                      maxWidth: '100%',
                                      minWidth: 0
                                    }
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedApiKeys.indexOf(apiKey.id) > -1}
                                    sx={{ mr: 1, flexShrink: 0 }}
                                  />
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    width: '100%',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden'
                                  }}>
                                    <KeyIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                                    <Box sx={{ 
                                      flexGrow: 1, 
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 500,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {apiKey.key_name}
                                      </Typography>
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          display: 'block'
                                        }}
                                      >
                                        {apiKey.key_preview} • {apiKey.last_used_at ? 'Usado em ' + new Date(apiKey.last_used_at).toLocaleDateString() : 'Nunca usado'}
                                      </Typography>
                                    </Box>
                                    {!apiKey.is_active && (
                                      <Chip 
                                        label="Inativa" 
                                        size="small" 
                                        color="error" 
                                        variant="outlined"
                                        sx={{ flexShrink: 0 }}
                                      />
                                    )}
                                  </Box>
                                </MenuItem>
                                ))
                              )}
                            </Select>
                            <FormHelperText>
                              {availableApiKeys.length === 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    Nenhuma API Key encontrada.
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => window.open('/api-keys', '_blank')}
                                    sx={{ minWidth: 'auto', p: 0.5, fontSize: '0.75rem' }}
                                  >
                                    Criar
                                  </Button>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {selectedApiKeys.length} de {availableApiKeys.length} selecionadas
                                </Typography>
                              )}
                            </FormHelperText>
                          </FormControl>
                        )}

                        <TextField
                          fullWidth
                          label="Descrição"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          multiline
                          rows={2}
                          placeholder="Descreva brevemente o propósito do agente..."
                          sx={{ maxWidth: '100%', minWidth: 0 }}
                        />
                        
                        {/* 🎯 Configuração de Streaming e Delay (Otimizado) */}
                        <Card sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                          {/* Primeira linha: Streaming e Delay lado a lado */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 2 }}>
                            {/* Streaming */}
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <SpeedIcon color="primary" />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Streaming
                                  </Typography>
                                </Box>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formData.streaming_enabled}
                                      onChange={(e) => handleInputChange('streaming_enabled', e.target.checked)}
                                      color="primary"
                                      size="small"
                                      disabled={outputFormat === 'structured'}
                                    />
                                  }
                                  label=""
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formData.streaming_enabled ? 'Resposta "digitada" em tempo real' : 'Resposta completa de uma vez'}
                              </Typography>
                            </Box>

                            {/* Delay */}
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AccessTimeIcon sx={{ color: 'warning.main' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Buffer Mensagens
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                  type="number"
                                  value={formData.message_delay_seconds || 0}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (value >= 0 && value <= 10) {
                                      handleInputChange('message_delay_seconds', value);
                                    }
                                  }}
                                  inputProps={{ min: 0, max: 10, step: 0.5 }}
                                  size="small"
                                  sx={{ width: '80px' }}
                                  disabled={outputFormat === 'structured'}
                                  InputProps={{
                                    endAdornment: <Typography variant="caption" color="text.secondary">s</Typography>
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {formData.message_delay_seconds > 0 ? `Aguarda ${formData.message_delay_seconds}s` : 'Imediato'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Avisos Informativos */}
                          {outputFormat === 'structured' && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                <strong>Streaming e Buffer desabilitados</strong>
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                Structured Outputs retorna JSON completo e validado de uma vez. 
                                Não é possível "streamar" meio objeto JSON (seria inválido).
                              </Typography>
                            </Alert>
                          )}
                          
                          {(formData.streaming_enabled && formData.message_delay_seconds > 0 && outputFormat !== 'structured') && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                ✨ <strong>Modo Híbrido:</strong> Streaming com Buffer ativo! 
                                Mensagens serão combinadas ({formData.message_delay_seconds}s) e depois transmitidas via streaming em tempo real.
                              </Typography>
                            </Alert>
                          )}

                          {/* Status atual */}
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(25, 118, 210, 0.2)' }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>📡 Modo atual:</strong> {' '}
                              {formData.streaming_enabled && formData.message_delay_seconds > 0 
                                ? `✨ Streaming + Buffer (${formData.message_delay_seconds}s) - Modo Híbrido`
                                : formData.streaming_enabled 
                                  ? `⚡ Streaming HTTP - Resposta em tempo real` 
                                  : formData.message_delay_seconds > 0 
                                    ? `🔄 Buffer HTTP (${formData.message_delay_seconds}s) - Combina mensagens`
                                    : `📝 HTTP tradicional - Resposta direta`
                              }
                            </Typography>
                          </Box>
                        </Card>
                        
                        <TextField
                          fullWidth
                          label="Comportamento do Agente"
                          value={formData.instructions}
                          onChange={(e) => handleInputChange('instructions', e.target.value)}
                          multiline
                          rows={20}
                          placeholder="Defina a personalidade, comportamento e instruções específicas para o agente..."
                          required
                          sx={{
                            maxWidth: '100%', 
                            minWidth: 0,
                            '& .MuiInputBase-input': {
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Dica: Use placeholders como {'{nome_usuario}'}, {'{empresa}'} para personalização
                        </Typography>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* 🆕 Seção Output Format */}
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="output-format-content"
                      id="output-format-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Output Format
                        </Typography>
                        <Tooltip 
                          title={
                            <Box sx={{ p: 1.5, maxWidth: 420 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff', fontSize: '0.9rem' }}>
                                Formato de Saída do Agente
                              </Typography>
                              
                              {/* Texto Simples */}
                              <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <TextFieldsIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                                    Texto Simples
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', color: '#e0e0e0', lineHeight: 1.5, mb: 0.8 }}>
                                  Respostas em linguagem natural, como uma conversa humana.
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#bdbdbd', fontStyle: 'italic', mb: 0.5 }}>
                                  <strong>Use quando:</strong>
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ color: '#bdbdbd', pl: 1 }}>
                                  • Assistentes virtuais e chatbots<br/>
                                  • Suporte ao cliente em tempo real<br/>
                                  • Respostas que serão lidas por pessoas<br/>
                                  • Geração de conteúdo e textos longos
                                </Typography>
                              </Box>
                              
                              {/* JSON Estruturado */}
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <CodeIcon sx={{ fontSize: 18, color: '#2196F3' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196F3' }}>
                                    JSON Estruturado
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', color: '#e0e0e0', lineHeight: 1.5, mb: 0.8 }}>
                                  Dados em formato JSON com schema predefinido. 100% previsível e validado pela OpenAI.
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#bdbdbd', fontStyle: 'italic', mb: 0.5 }}>
                                  <strong>Use quando:</strong>
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ color: '#bdbdbd', pl: 1 }}>
                                  • Classificar tickets (categoria, prioridade, departamento)<br/>
                                  • Extrair dados de documentos (nome, email, telefone)<br/>
                                  • Analisar sentimento (positivo/neutro/negativo + score)<br/>
                                  • Integração com CRM, BI, bancos de dados<br/>
                                  • Automação de workflows e decisões baseadas em dados
                                </Typography>
                              </Box>
                              
                              <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                              
                              <Typography variant="caption" sx={{ display: 'block', color: '#90CAF9', fontStyle: 'italic', textAlign: 'center' }}>
                                Exemplo: Use Texto para "explique esse conceito" e JSON para "classifique esse ticket"
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="right"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#1e1e1e',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                '& .MuiTooltip-arrow': {
                                  color: '#1e1e1e',
                                },
                              },
                            },
                          }}
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                          Configure se o agente deve retornar texto simples ou JSON estruturado
                        </Typography>
                        
                        {/* Alerta de Streaming Desabilitado */}
                        {outputFormat === 'structured' && (
                          <Alert severity="warning" variant="outlined">
                            <Typography variant="body2">
                              <strong>Streaming HTTP foi desabilitado automaticamente.</strong>
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              Structured Outputs retorna JSON completo e validado de uma vez. 
                              Não é possível "streamar" meio objeto JSON (seria inválido).
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                              Buffer de Mensagens ainda funciona normalmente para consolidar contexto.
                            </Typography>
                          </Alert>
                        )}
                        
                        {/* Alerta de Tools + Structured Output */}
                        {outputFormat === 'structured' && formData.tools_config && formData.tools_config.length > 0 && (
                          <Alert severity="info" variant="outlined">
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Este agente possui {formData.tools_config.length} ferramenta(s) configurada(s).</strong>
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                              Verifique se faz sentido retornar JSON estruturado:
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                              <Typography variant="caption" component="div" sx={{ color: 'success.main', fontWeight: 600, mb: 0.5 }}>
                                ✅ Use structured output se o agente:
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ pl: 1, mb: 0.3 }}>
                                • Analisa/classifica dados após usar as tools
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ pl: 1, mb: 1 }}>
                                • Retorno é para sistemas (APIs, automação, CRM)
                              </Typography>
                              
                              <Typography variant="caption" component="div" sx={{ color: 'error.main', fontWeight: 600, mb: 0.5 }}>
                                ❌ Use texto simples se o agente:
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ pl: 1, mb: 0.3 }}>
                                • Conversa com usuários finais
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ pl: 1 }}>
                                • Usa tools para enriquecer a conversa
                              </Typography>
                            </Box>
                            <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                              <strong>Exemplo bom:</strong> Agente usa "consultar_historico" e retorna {`{"risco": "baixo", "aprovado": true}`}
                            </Typography>
                          </Alert>
                        )}
                        
                        {/* Toggle Text vs Structured */}
                        <FormControl fullWidth>
                          <InputLabel>Formato de Saída</InputLabel>
                          <Select
                            value={outputFormat}
                            onChange={(e) => {
                              const newFormat = e.target.value;
                              setOutputFormat(newFormat);
                              if (newFormat === 'text') {
                                handleRemoveSchema();
                              }
                            }}
                            label="Formato de Saída"
                          >
                            <MenuItem value="text">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextFieldsIcon fontSize="small" />
                                <Typography>Texto Simples</Typography>
                                <Chip label="Padrão" size="small" />
                              </Box>
                            </MenuItem>
                            <MenuItem value="structured">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CodeIcon fontSize="small" />
                                <Typography>JSON Estruturado</Typography>
                                <Chip label="Avançado" size="small" color="primary" />
                              </Box>
                            </MenuItem>
                          </Select>
                          <FormHelperText>
                            {outputFormat === 'text' 
                              ? 'O agente retornará texto simples (comportamento atual)'
                              : 'O agente retornará JSON estruturado com schema definido (usando Structured Outputs da OpenAI)'}
                          </FormHelperText>
                        </FormControl>
                        
                        {/* Editor de Schema */}
                        {outputFormat === 'structured' && (
                          <Box>
                            {formData.output_type_config ? (
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CodeIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      Schema: {formData.output_type_config.name}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Stack direction="row" spacing={1}>
                                    <Chip 
                                      label={`${formData.output_type_config.properties?.length || 0} campos`}
                                      size="small"
                                      color="primary"
                                    />
                                    <Button 
                                      size="small" 
                                      startIcon={<CodeIcon />}
                                      onClick={() => setSchemaEditorOpen(true)}
                                    >
                                      Editar
                                    </Button>
                                    <IconButton 
                                      size="small" 
                                      color="error"
                                      onClick={handleRemoveSchema}
                                      sx={{ ml: 1 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                  </Box>
                                </Box>
                                
                                <Divider sx={{ my: 1 }} />
                                
                                {/* Preview do schema */}
                                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                                  <Typography 
                                    variant="caption" 
                                    component="pre" 
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      margin: 0,
                                      fontFamily: '"Cascadia Code", "Fira Code", Monaco, monospace'
                                    }}
                                  >
                                    {JSON.stringify(formData.output_type_config, null, 2)}
                                  </Typography>
                                </Box>
                              </Paper>
                            ) : (
                              <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<AddIcon />}
                                onClick={() => setSchemaEditorOpen(true)}
                                sx={{ py: 2 }}
                              >
                                Definir Schema de Saída
                              </Button>
                            )}
                            
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2">
                                <strong>Dica:</strong> Use Structured Outputs para classificação, extração de dados, 
                                ou qualquer caso onde você precise de um formato JSON específico e previsível.
                              </Typography>
                            </Alert>
                          </Box>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Seção Comportamento e LLM */}
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="llm-content"
                      id="llm-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MemoryIcon color="secondary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Comportamento e LLM
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        {/* Todos os componentes do modelo em linha única */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1.5, 
                          alignItems: 'flex-end', 
                          flexWrap: 'wrap',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}>
                          {/* Modelo LLM */}
                          <Box sx={{ 
                            minWidth: 0, 
                            maxWidth: 220, 
                            flexGrow: 1,
                            flexBasis: '180px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                              <FormControl fullWidth size="small" sx={{ maxWidth: '100%', overflow: 'hidden', minWidth: 0 }}>
                                <InputLabel>Modelo LLM</InputLabel>
                                <Select
                                  value={formData.llm_config?.model || ''}
                                  onChange={(e) => handleInputChange('llm_config.model', e.target.value)}
                                  label="Modelo LLM"
                                >
                                  {availableModels.map((model) => (
                                    <MenuItem key={model.value} value={model.value}>
                                      <Typography variant="body2">
                                        {model.label}
                                      </Typography>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Tooltip
                                title="Modelo de linguagem usado pelo agente. Modelos mais avançados custam mais."
                                placement="top"
                              >
                                <InfoIcon color="action" fontSize="small" sx={{ mb: 1 }} />
                              </Tooltip>
                            </Box>
                          </Box>
                          {/* Temperatura com slider compacto */}
                          <Box sx={{ 
                            minWidth: 0, 
                            maxWidth: 160, 
                            flexGrow: 1,
                            flexBasis: '140px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                                  Temp: {formData.llm_config.temperature}
                                </Typography>
                                <Slider
                                  value={formData.llm_config.temperature}
                                  onChange={(e, value) => handleInputChange('llm_config.temperature', value)}
                                  min={0}
                                  max={2}
                                  step={0.1}
                                  size="small"
                                  valueLabelDisplay="auto"
                                  sx={{ mt: 1 }}
                                />
                              </Box>
                              <Tooltip
                                title="Controla a criatividade das respostas. 0 = respostas consistentes, 2 = respostas criativas."
                                placement="top"
                              >
                                <InfoIcon color="action" fontSize="small" sx={{ mb: 1 }} />
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Max Tokens */}
                          <Box sx={{ 
                            minWidth: 0, 
                            maxWidth: 130, 
                            flexGrow: 1,
                            flexBasis: '110px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Max Tokens"
                                value={formData.llm_config.max_tokens}
                                onChange={(e) => handleInputChange('llm_config.max_tokens', parseInt(e.target.value))}
                                inputProps={{ min: 1, max: 4000 }}
                              />
                              <Tooltip
                                title="Limite máximo de tokens na resposta. Valores maiores = respostas mais longas, mas custam mais."
                                placement="top"
                              >
                                <InfoIcon color="action" fontSize="small" sx={{ mb: 1 }} />
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Top P */}
                          <Box sx={{ 
                            minWidth: 0, 
                            maxWidth: 110, 
                            flexGrow: 1,
                            flexBasis: '90px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Top P"
                                value={formData.llm_config.top_p}
                                onChange={(e) => handleInputChange('llm_config.top_p', parseFloat(e.target.value))}
                                inputProps={{ min: 0, max: 1, step: 0.1 }}
                              />
                              <Tooltip
                                title="Controla diversidade do vocabulário. Valores menores = palavras mais comuns."
                                placement="top"
                              >
                                <InfoIcon color="action" fontSize="small" sx={{ mb: 1 }} />
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Presence Penalty */}
                          <Box sx={{ 
                            minWidth: 0, 
                            maxWidth: 150, 
                            flexGrow: 1,
                            flexBasis: '120px'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Presence Penalty"
                                value={formData.llm_config.presence_penalty}
                                onChange={(e) => handleInputChange('llm_config.presence_penalty', parseFloat(e.target.value))}
                                inputProps={{ min: -2, max: 2, step: 0.1 }}
                              />
                              <Tooltip
                                title="Penaliza repetição de tópicos. Valores positivos incentivam tópicos novos."
                                placement="top"
                              >
                                <InfoIcon color="action" fontSize="small" sx={{ mb: 1 }} />
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Seção Bases de Conhecimento */}
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="knowledge-content"
                      id="knowledge-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KnowledgeIcon color="success" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Bases de Conhecimento
                        </Typography>
                        {agentKnowledgeBases?.enabled_count > 0 && (
                          <Chip
                            label={`${agentKnowledgeBases.enabled_count} ativas`}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        <Typography variant="body2" color="text.secondary">
                          Documentos e informações da empresa para consulta dos agentes.
                        </Typography>

                        {/* Mensagens de erro para bases de conhecimento */}
                        {kbError && (
                          <Alert severity="error" onClose={() => setKbError(null)}>
                            {kbError}
                          </Alert>
                        )}

                        {!agentId ? (
                          <Alert severity="info">
                            <Typography variant="body2">
                              💡 Salve o agente primeiro para configurar bases de conhecimento.
                            </Typography>
                          </Alert>
                        ) : kbLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : !agentKnowledgeBases || agentKnowledgeBases.available_bases.length === 0 ? (
                          <Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                Nenhuma base de conhecimento habilitada para este agente.
                              </Typography>
                            </Alert>

                            {/* Card de ação dentro do mesmo grid usado quando já existem bases */}
                            <Box 
                              sx={{ 
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  sm: 'repeat(2, 1fr)',
                                  md: 'repeat(3, 1fr)',
                                  lg: 'repeat(4, 1fr)',
                                },
                                gap: 3,
                                maxWidth: '100%',
                              }}
                            >
                              <Card
                                sx={{
                                  height: '140px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  border: '2px dashed',
                                  borderColor: 'primary.main',
                                  backgroundColor: 'rgba(79, 172, 254, 0.05)',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: 'primary.dark',
                                    backgroundColor: 'rgba(79, 172, 254, 0.1)',
                                    transform: 'translateY(-2px)',
                                  }
                                }}
                                onClick={handleOpenAddKbDialog}
                              >
                                <Box sx={{ textAlign: 'center' }}>
                                  <AddIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    Adicionar Base
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Habilitar nova base de conhecimento
                                  </Typography>
                                </Box>
                              </Card>
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                              },
                              gap: 3,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              '& > *': {
                                minWidth: 0,
                                maxWidth: '100%'
                              }
                            }}
                          >
                            {/* Renderizar bases ativas */}
                            {agentKnowledgeBases.available_bases.map((kb, index) => (
                              <motion.div
                                key={kb.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                style={{ 
                                  maxWidth: '100%', 
                                  minWidth: 0, 
                                  overflow: 'hidden' 
                                }}
                              >
                                <Card
                                  sx={{
                                    height: '140px', // Altura fixa mais compacta
                                    display: 'flex',
                                    flexDirection: 'column',
                                    background: kb.is_enabled 
                                      ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                                      : 'background.paper',
                                    color: kb.is_enabled ? 'white' : 'text.primary',
                                    border: '1px solid',
                                    borderColor: kb.is_enabled ? 'transparent' : 'divider',
                                    transition: 'all 0.3s ease',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: kb.is_enabled 
                                        ? '0 8px 25px rgba(79, 172, 254, 0.3)'
                                        : '0 6px 20px rgba(0,0,0,0.1)',
                                    },
                                  }}
                                >
                                  <CardContent sx={{ 
                                    p: 1.5, 
                                    flexGrow: 1, 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    maxWidth: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden'
                                  }}>
                                    {/* Header compacto */}
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between', 
                                      mb: 1,
                                      maxWidth: '100%',
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexGrow: 1 }}>
                                        <Box
                                          sx={{
                                            p: 0.5,
                                            borderRadius: '50%',
                                            bgcolor: kb.is_enabled ? 'rgba(255,255,255,0.2)' : 'rgba(79, 172, 254, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <FolderIcon 
                                            sx={{ 
                                              color: kb.is_enabled ? 'white' : '#4facfe',
                                              fontSize: 16 
                                            }} 
                                          />
                                        </Box>
                                        
                                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                          <Typography 
                                            variant="subtitle2" 
                                            sx={{ 
                                              fontWeight: 600,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              fontSize: '0.875rem',
                                              color: 'inherit',
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {kb.name}
                                          </Typography>
                                          <Typography 
                                            variant="caption" 
                                            sx={{ 
                                              display: 'block',
                                              color: kb.is_enabled ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                                              fontSize: '0.7rem',
                                            }}
                                          >
                                            {kb.documents_count || 0} docs • {kb.chunks_count || 0} chunks
                                          </Typography>
                                        </Box>
                                      </Box>

                                      <Switch
                                        checked={kb.is_enabled}
                                        onChange={(e) => handleKnowledgeBaseToggle(kb.id, e.target.checked)}
                                        size="small"
                                        disabled={kbLoading}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: 'white',
                                          },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: 'rgba(255,255,255,0.3)',
                                          },
                                        }}
                                      />
                                    </Box>

                                    {/* Descrição compacta com tooltip */}
                                    <Box sx={{ 
                                      flexGrow: 1, 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between',
                                      maxWidth: '100%',
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontSize: '0.75rem',
                                          lineHeight: 1.3,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          color: kb.is_enabled ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                                          flexGrow: 1,
                                          mr: 1,
                                        }}
                                      >
                                        {kb.description || `Base de conhecimento com documentos sobre ${kb.name.toLowerCase()}`}
                                      </Typography>

                                      {/* Ícone de info com tooltip */}
                                      <Tooltip 
                                        title={
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                              {kb.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                              {kb.description || `Base de conhecimento com documentos sobre ${kb.name.toLowerCase()}`}
                                            </Typography>
                                            <Typography variant="caption">
                                              Tool: {kb.tool_name}<br/>
                                              Documentos: {kb.documents_count || 0}<br/>
                                              Chunks: {kb.chunks_count || 0}
                                            </Typography>
                                          </Box>
                                        }
                                        placement="top"
                                      >
                                        <IconButton 
                                          size="small"
                                          sx={{ 
                                            color: kb.is_enabled ? 'rgba(255,255,255,0.7)' : 'text.disabled',
                                            p: 0.5,
                                          }}
                                        >
                                          <InfoIcon fontSize="small" sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>

                                    {/* Footer compacto */}
                                    <Box sx={{ mt: 'auto' }}>
                                      <Chip
                                        label={kb.tool_name}
                                        size="small"
                                        sx={{ 
                                          fontSize: '0.65rem', 
                                          height: 20,
                                          backgroundColor: kb.is_enabled ? 'rgba(255,255,255,0.2)' : 'rgba(79, 172, 254, 0.1)',
                                          color: kb.is_enabled ? 'white' : '#4facfe',
                                          fontWeight: 500,
                                        }}
                                      />
                                    </Box>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            ))}
                            
                            {/* 🚀 BOTÃO OTIMIZADO: Adicionar Base */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: agentKnowledgeBases.available_bases.length * 0.1 }}
                              style={{ 
                                maxWidth: '100%', 
                                minWidth: 0, 
                                overflow: 'hidden' 
                              }}
                            >
                              <Card
                                sx={{
                                  height: '140px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  border: '2px dashed',
                                  borderColor: 'primary.main',
                                  backgroundColor: 'rgba(79, 172, 254, 0.05)',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    borderColor: 'primary.dark',
                                    backgroundColor: 'rgba(79, 172, 254, 0.1)',
                                    transform: 'translateY(-2px)',
                                  }
                                }}
                                onClick={handleOpenAddKbDialog}
                              >
                                <Box sx={{ textAlign: 'center' }}>
                                  <AddIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    Adicionar Base
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Habilitar nova base de conhecimento
                                  </Typography>
                                </Box>
                              </Card>
                            </motion.div>
                          </Box>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Seção Ferramentas e Integrações */}
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="tools-content"
                      id="tools-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BuildIcon color="warning" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Ferramentas e Integrações
                        </Typography>
                        {agentIntegrations.length > 0 && (
                          <Chip
                            label={`${agentIntegrations.length} ativas`}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ToolsConfiguration
                        agentId={agentId}
                        toolsConfig={formData.tools_config}
                        onToolsConfigChange={(tools) => handleInputChange('tools_config', tools)}
                        availableTools={availableTools}
                        agentIntegrations={agentIntegrations}
                        onIntegrationsChange={setAgentIntegrations}
                        onAutoSave={handleAutoSave}
                        isNewAgent={!isEditMode}
                        formData={formData}
                        hideKnowledgeBases={true}
                        agentKnowledgeBases={agentKnowledgeBases}
                        onKnowledgeBasesChange={handleKnowledgeBasesChange}
                        kbLoading={kbLoading}
                        kbError={kbError}
                        onClearKbError={handleClearKbError}
                      />
                    </AccordionDetails>
                  </Accordion>

                {/* Conversas recentes (accordion) removido: agora está na aba "Conversas" */}

                  {/* Seção Segurança e Controles */}
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="security-content"
                      id="security-header"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShieldIcon color="error" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Segurança e Controles
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={3}>
                        <Alert severity="info">
                          Configurações de segurança (guardrails, handoffs, workflows) serão implementadas na próxima versão
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          Em breve você poderá configurar:
                        </Typography>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="body2" color="text.secondary">• Filtros de conteúdo</Typography>
                          <Typography variant="body2" color="text.secondary">• Limites de interação</Typography>
                          <Typography variant="body2" color="text.secondary">• Handoffs para outros agentes</Typography>
                          <Typography variant="body2" color="text.secondary">• Workflows automatizados</Typography>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </motion.div>
            )}

            {/* Aba Conversas */}
            {currentTab === 'sessions' && isEditMode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AgentSessionsTab agentId={agentId} />
              </motion.div>
            )}

            {/* Aba Performance/Diagnóstico */}
            {currentTab === 'performance' && isEditMode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <AgentDiagnosticsV5 agentId={agentId} agent={formData} />
              </motion.div>
            )}

            {/* Aba Coleta de Dados */}
            {currentTab === 'data_collection' && isEditMode && hasDataCollection && (
              <DataCollectionTab 
                agentId={agentId} 
                agentName={formData.name || 'Agente sem nome'} 
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Drawer do Teste do Agente */}
      <Drawer
        anchor="right"
        open={testMode}
        onClose={() => setTestMode(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '480px', md: '520px' },
            maxWidth: '90vw',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        ModalProps={{
          keepMounted: true, // Melhor performance em mobile
        }}
        transitionDuration={300}
      >
        {/* Header do Drawer */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon color="primary" />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Teste do Agente
                </Typography>
                {/* 🎯 UX MELHORIA: Tag de streaming quando habilitado */}
                {formData.streaming_enabled && (
                  <Chip
                    label="⚡ Streaming"
                    size="small"
                    color="secondary"
                    sx={{
                      fontSize: '0.7rem',
                      height: 20,
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formData.name || 'Agente sem nome'}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setTestMode(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Subtítulo */}
        <Box sx={{ 
          px: 2, 
          py: 1, 
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              💬 Converse com o agente para testar seu comportamento
            </Typography>
            {testMessages.length > 0 && (
              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                {testMessages.length} mensagens na conversa
              </Typography>
            )}
          </Box>
          
          {/* Botão Nova Conversa */}
          {testMessages.length > 0 && (
            <Tooltip title="Iniciar nova conversa (limpa o histórico)">
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleNewConversation}
                sx={{
                  borderColor: 'primary.light',
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  },
                }}
              >
                Nova
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Conteúdo do Teste */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0,
        }}>
          <AgentTester
            agentId={agentId}
            messages={testMessages}
            onSendMessage={handleTestAgent}
            onNewConversation={handleNewConversation}
            onTraceData={handleTraceData}
            disabled={!isEditMode || loading}
            streamingEnabled={formData.streaming_enabled} // 🆕 Passar status de streaming
            messageDelay={formData.message_delay_seconds || 0} // 🆕 Passar delay configurado
          />
        </Box>
      </Drawer>

      {/* Botões FAB Fixos */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          right: (testMode || codeGeneratorOpen) ? { xs: 24, sm: '550px', md: '720px' } : 24, // Afastar quando drawer aberto
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 1200,
          transition: 'right 0.3s ease',
        }}
      >




        {/* FAB Gerador de Código */}
        {isEditMode && (
          <Tooltip title="Gerar código de integração" placement="left">
            <Fab
              onClick={() => setCodeGeneratorOpen(true)}
              size="medium"
              variant="extended"
              sx={{
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                bgcolor: '#2196f3',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1976d2',
                  transform: 'translateX(-4px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ApiIcon sx={{ mr: 1 }} />
              Código
            </Fab>
          </Tooltip>
        )}

        {/* FAB Testar Agente */}
        {isEditMode && (
          <Tooltip title="Testar agente (Ctrl+T)" placement="left">
            <Fab
              onClick={() => setTestMode(!testMode)}
              size="medium"
              variant="extended"
              sx={{
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                bgcolor: '#9c27b0', // Roxo igual ao botão fechar
                color: 'white',
                display: { xs: testMode ? 'none' : 'flex', sm: 'flex' },
                '&:hover': {
                  bgcolor: '#7b1fa2',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ChatIcon sx={{ mr: 1 }} />
              {testMode ? "Fechar" : "Testar"}
            </Fab>
          </Tooltip>
        )}
      </Box>

      {/* Drawer do Gerador de Código */}
      <CodeGeneratorDrawer 
        open={codeGeneratorOpen}
        onClose={() => setCodeGeneratorOpen(false)}
        agentName={formData.name}
        formData={formData} // 🆕 Passar formData para detectar streaming
        availableApiKeys={availableApiKeys}
        selectedApiKeys={selectedApiKeys}
        setSelectedApiKeys={setSelectedApiKeys}
        generatedCode={generatedCode}
        setGeneratedCode={setGeneratedCode}
        codeLanguage={codeLanguage}
        setCodeLanguage={setCodeLanguage}
        documentationOpen={documentationOpen}
        setDocumentationOpen={setDocumentationOpen}
      />

      {/* Drawer da Documentação */}
      <DocumentationDrawer
        open={documentationOpen}
        onClose={() => setDocumentationOpen(false)}
        agentName={formData.name}
        responseExample={generatedCode.responseExample}
        companyInfo={companyInfo}
        isStreamingAgent={formData.streaming_enabled || false}
        outputSchema={formData.output_type_config}
      />

      {/* Drawer de Trace */}
      <TraceDrawer
        open={traceMode}
        onClose={() => setTraceMode(false)}
        traceData={traceData}
      />

      {/* 🚀 DIALOG OTIMIZADO: Adicionar Base de Conhecimento */}
      <Dialog
        open={addKbDialogOpen}
        onClose={handleCloseAddKbDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Adicionar Base de Conhecimento
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Selecione uma base de conhecimento para habilitar neste agente
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {loadingAvailableBases ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableKnowledgeBases.length === 0 ? (
            <Alert severity="info">
              <Typography variant="body2">
                Todas as bases de conhecimento disponíveis já estão habilitadas para este agente.
              </Typography>
            </Alert>
          ) : (
            <Box 
              sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
                maxHeight: '400px',
                overflow: 'auto'
              }}
            >
              {availableKnowledgeBases.map((kb) => (
                <Card
                  key={kb.id}
                  sx={{
                    height: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(79, 172, 254, 0.2)',
                    }
                  }}
                  onClick={() => handleAddKnowledgeBase(kb.id)}
                >
                  <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: '50%',
                          bgcolor: 'rgba(79, 172, 254, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FolderIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {kb.name}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {kb.description || `Base de conhecimento com documentos sobre ${kb.name.toLowerCase()}`}
                    </Typography>

                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={kb.tool_name}
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem', 
                          height: 20,
                          backgroundColor: 'rgba(79, 172, 254, 0.1)',
                          color: 'primary.main',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {kb.documents_count} docs
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseAddKbDialog}
            disabled={addingKnowledgeBase}
          >
            Cancelar
          </Button>
          {addingKnowledgeBase && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Habilitando...
              </Typography>
            </Box>
          )}
        </DialogActions>
      </Dialog>
      
      {/* 🆕 Dialog do Editor de Schema */}
      <OutputSchemaEditor
        open={schemaEditorOpen}
        onClose={() => setSchemaEditorOpen(false)}
        initialSchema={formData.output_type_config}
        onSave={handleSaveSchema}
      />
    </Box>
  );
};

export default CreateAgent; 